"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { getPortDisplayName } from "@/lib/greekPortsMap";

type ChatMessage = { role: "user" | "assistant"; content: string };
type Trip = Record<string, unknown>;

function FormattedMessage({ content }: { content: string }) {
  const lines = content.split("\n");

  // Helper: Parse inline bold markdown (**text**)
  const parseBold = (text: string) => {
    const parts = text.split(/(\*\*.+?\*\*)/g);
    return parts.map((part, idx) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        const innerText = part.slice(2, -2);
        if (innerText) {
          return (
            <span key={idx} className="font-semibold">
              {innerText}
            </span>
          );
        }
      }
      return <span key={idx}>{part}</span>;
    });
  };

  return (
    <>
      {lines.map((line, idx) => {
        const trimmed = line.trim();

        // Empty line spacing
        if (!trimmed) return <div key={idx} className="h-2" />;

        // Section header (lines ending with :)
        if (trimmed.endsWith(":") && !trimmed.startsWith("•")) {
          return (
            <div
              key={idx}
              className="mt-2 font-semibold text-neutral-800 first:mt-0"
            >
              {parseBold(trimmed)}
            </div>
          );
        }

        // Bullet point
        if (trimmed.startsWith("•")) {
          const bulletContent = trimmed.slice(1).trim();
          return (
            <div key={idx} className="flex gap-2">
              <span className="text-neutral-500">•</span>
              <span className="flex-1">{parseBold(bulletContent)}</span>
            </div>
          );
        }

        // Regular line
        return <div key={idx}>{parseBold(line)}</div>;
      })}
    </>
  );
}

function formatMoneyFromCents(value: number | null) {
  if (typeof value !== "number" || !Number.isFinite(value)) return "Price varies";
  return `€${(value / 100).toFixed(2)}`;
}

function minAccommodationPriceCents(trip: Trip) {
  const segments =
    (trip as { segments?: Array<Record<string, unknown>> }).segments ?? [];
  const prices = segments
    .flatMap((segment) =>
      (
        (segment as { accommodations?: Array<Record<string, unknown>> })
          .accommodations ?? []
      ).map(
        (item) =>
          (item as { expectedPrice?: { totalPriceInCents?: number } })
            .expectedPrice?.totalPriceInCents
      )
    )
    .filter((v): v is number => typeof v === "number" && Number.isFinite(v));
  return prices.length ? Math.min(...prices) : null;
}

function getTimeUntilDeparture(departureDateTime: string | undefined) {
  if (!departureDateTime) return null;
  try {
    const departure = new Date(departureDateTime);
    const now = new Date();
    const diffMs = departure.getTime() - now.getTime();
    if (diffMs < 0) return null;
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    if (hours < 24) return `Departs in ${hours}h`;
    const days = Math.floor(hours / 24);
    return `Departs in ${days}d`;
  } catch {
    return null;
  }
}

function calculateDuration(dep?: string, arr?: string): string {
  if (!dep || !arr) return "";
  try {
    const departure = new Date(dep);
    const arrival = new Date(arr);
    const diffMs = arrival.getTime() - departure.getTime();
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    if (hours > 0 && minutes > 0) return `${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h`;
    return `${minutes}m`;
  } catch {
    return "";
  }
}

function formatTime(datetime: string | undefined): string {
  if (!datetime) return "Unknown time";
  try {
    const date = new Date(datetime);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  } catch {
    return "Unknown time";
  }
}

function addAffiliateCode(url: string | undefined) {
  if (!url) return null;
  try {
    const urlObj = new URL(url);
    urlObj.searchParams.set('aff_uid', 'dscvgrc');
    return urlObj.toString();
  } catch {
    return url;
  }
}

function getIDSCodeFromDeepLink(deepLink: string | undefined): string | null {
  if (!deepLink) return null;
  try {
    return new URL(deepLink).searchParams.get('IDSCode');
  } catch {
    return null;
  }
}

function buildRoundTripUrl(
  departureCode: string,
  arrivalCode: string,
  departureDate: string,
  returnDate: string,
  outboundIDSCode: string,
  returnIDSCode: string
): string {
  // Format dates as YYYYMMDD (remove dashes)
  const formattedDepartureDate = departureDate.replace(/-/g, '');
  const formattedReturnDate = returnDate.replace(/-/g, '');

  // Build Ferryhopper round trip URL with correct format
  // itinerary: departure,arrival,departure (back to origin)
  // IDSCode: outbound trip code,return trip code
  // dates: departureDate,returnDate
  const url = `https://www.ferryhopper.com/booking/results?itinerary=${departureCode},${arrivalCode},${departureCode}&IDSCode=${outboundIDSCode},${returnIDSCode}&dates=${formattedDepartureDate},${formattedReturnDate}`;
  return addAffiliateCode(url) || url;
}

function getVehicleTypes(trip: Trip): string[] {
  const vehicles = (trip as { vehicles?: Array<{ type?: string }> }).vehicles ?? [];
  const types = new Set(
    vehicles.map(v => v.type).filter((t): t is string => typeof t === 'string')
  );
  return Array.from(types);
}

function getVehicleEmoji(type: string): string {
  switch(type.toUpperCase()) {
    case 'CAR': return '🚗';
    case 'MOTORBIKE': return '🏍️';
    case 'MOTORHOME': return '🚐';
    default: return '🚗';
  }
}

function isCabin(description: string): boolean {
  return description.toLowerCase().includes('cabin');
}

function formatAccommodationType(type: string): string {
  return type
    .split('_')
    .filter(Boolean)
    .map(word => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');
}

async function checkNetworkConnection(): Promise<boolean> {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return false;

  try {
    // Quick ping to verify internet connectivity
    await fetch('https://www.google.com/favicon.ico', {
      mode: 'no-cors',
      cache: 'no-cache'
    });
    return true;
  } catch {
    return false;
  }
}

function parsePortCodesFromDeepLink(deepLink: string): { departureCode: string; arrivalCode: string } | null {
  try {
    // The deepLink has the itinerary in the hash fragment like: #/results?itinerary=PIR,JMK
    // We need to parse the hash part
    const url = new URL(deepLink);

    // Try to get from regular query params first
    let itinerary = url.searchParams.get('itinerary');

    // If not found, try the hash fragment
    if (!itinerary && url.hash) {
      // Extract query string from hash (e.g., "#/results?itinerary=PIR,JMK&...")
      const hashQueryMatch = url.hash.match(/\?(.+)/);
      if (hashQueryMatch) {
        const hashParams = new URLSearchParams(hashQueryMatch[1]);
        itinerary = hashParams.get('itinerary');
      }
    }

    if (!itinerary) return null;

    const [departureCode, arrivalCode] = itinerary.split(',');
    return { departureCode, arrivalCode };
  } catch {
    return null;
  }
}

function extractSearchParamsFromTrips(trips: Trip[]): {
  departurePort: string;
  arrivalPort: string;
  departurePortCode: string;
  arrivalPortCode: string;
  date: string;
} | null {
  if (trips.length === 0) return null;

  const firstTrip = trips[0];
  console.log('extractSearchParamsFromTrips - firstTrip:', firstTrip);

  const segments = (firstTrip as { segments?: Array<Record<string, unknown>> }).segments ?? [];
  const firstSegment = segments[0];

  if (!firstSegment) return null;

  const depPort = (firstSegment as { departurePort?: { name?: string; code?: string } }).departurePort;
  const arrPort = (firstSegment as { arrivalPort?: { name?: string; code?: string } }).arrivalPort;
  const depDateTime = (firstSegment as { departureDateTime?: string }).departureDateTime;

  console.log('extractSearchParamsFromTrips - depPort:', depPort);
  console.log('extractSearchParamsFromTrips - arrPort:', arrPort);

  // Extract port codes from deepLink
  const deepLink = (firstTrip as { deepLink?: string }).deepLink;
  console.log('extractSearchParamsFromTrips - deepLink:', deepLink);
  const portCodes = deepLink ? parsePortCodesFromDeepLink(deepLink) : null;
  console.log('extractSearchParamsFromTrips - parsed port codes from deepLink:', portCodes);
  console.log('extractSearchParamsFromTrips - segment depPort.code:', depPort?.code);
  console.log('extractSearchParamsFromTrips - segment arrPort.code:', arrPort?.code);

  // Prioritize deepLink codes (original search params) over segment codes (specific ports)
  // This ensures we show "Athens (all ports)" when searched with ATH, not "Rafina, Athens"
  const departureCode = portCodes?.departureCode || depPort?.code || '';
  const arrivalCode = portCodes?.arrivalCode || arrPort?.code || '';

  console.log('extractSearchParamsFromTrips - final codes:', { departureCode, arrivalCode });

  // Use the static map to get display names for "all ports" codes
  // Falls back to segment port names for specific ports
  let departureName = getPortDisplayName(departureCode) || depPort?.name || '';
  let arrivalName = getPortDisplayName(arrivalCode) || arrPort?.name || '';

  console.log('extractSearchParamsFromTrips - final names:', { departureName, arrivalName });

  return {
    departurePort: departureName,
    arrivalPort: arrivalName,
    departurePortCode: departureCode,
    arrivalPortCode: arrivalCode,
    date: depDateTime ? depDateTime.substring(0, 10) : '',
  };
}

function formatDateDisplay(dateString: string): string {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
  } catch {
    return '';
  }
}

function updateDeepLinkDate(deepLink: string, newDate: string): string {
  try {
    // DeepLink format: https://www.ferryhopper.com/en/?...#/results?...&dates=20260615&...
    // We need to update the dates parameter in the hash fragment
    // newDate format: 2026-06-15, we need: 20260615
    const formattedDate = newDate.replace(/-/g, '');

    // Replace the dates parameter in the URL
    return deepLink.replace(/dates=\d{8}/, `dates=${formattedDate}`);
  } catch {
    return deepLink;
  }
}

function generateCalendarDays(month: Date): Array<{ day: number; date: string; isCurrentMonth: boolean }> {
  const year = month.getFullYear();
  const monthIndex = month.getMonth();

  const firstDay = new Date(year, monthIndex, 1);
  const lastDay = new Date(year, monthIndex + 1, 0);

  const days: Array<{ day: number; date: string; isCurrentMonth: boolean }> = [];

  // Helper to create date string in YYYY-MM-DD format without timezone issues
  const formatDate = (y: number, m: number, d: number) => {
    const month = String(m + 1).padStart(2, '0');
    const day = String(d).padStart(2, '0');
    return `${y}-${month}-${day}`;
  };

  // Add padding for days before month starts (Monday = 0)
  const startPadding = (firstDay.getDay() + 6) % 7;
  const prevMonthLastDay = new Date(year, monthIndex, 0);
  for (let i = startPadding - 1; i >= 0; i--) {
    const day = prevMonthLastDay.getDate() - i;
    days.push({
      day,
      date: formatDate(year, monthIndex - 1, day),
      isCurrentMonth: false
    });
  }

  // Add actual days of current month
  for (let i = 1; i <= lastDay.getDate(); i++) {
    days.push({
      day: i,
      date: formatDate(year, monthIndex, i),
      isCurrentMonth: true
    });
  }

  // Add padding for days after month ends
  const remainingDays = 42 - days.length; // 6 weeks total
  for (let i = 1; i <= remainingDays; i++) {
    days.push({
      day: i,
      date: formatDate(year, monthIndex + 1, i),
      isCurrentMonth: false
    });
  }

  return days;
}

type TripLegSummary = {
  depPort: string;
  depTime: string;
  arrPort: string;
  arrTime: string;
  priceCents: number | null;
  companyName: string;
  companyIconURL: string | null;
};

function getTripSummary(trip: Trip | null): TripLegSummary | null {
  if (!trip) return null;
  const segments = (trip as { segments?: Array<Record<string, unknown>> }).segments ?? [];
  const firstSegment = segments[0] as
    | {
        departurePort?: { name?: string };
        arrivalPort?: { name?: string };
        departureDateTime?: string;
        arrivalDateTime?: string;
        ownerCompany?: { name?: string; iconURL?: string };
        marketingCompany?: { name?: string; iconURL?: string };
      }
    | undefined;
  if (!firstSegment) return null;
  return {
    depPort: firstSegment.departurePort?.name ?? "Departure",
    depTime: formatTime(firstSegment.departureDateTime),
    arrPort: firstSegment.arrivalPort?.name ?? "Arrival",
    arrTime: formatTime(firstSegment.arrivalDateTime),
    priceCents: minAccommodationPriceCents(trip),
    companyName: firstSegment.marketingCompany?.name ?? firstSegment.ownerCompany?.name ?? "Ferry Company",
    companyIconURL: firstSegment.marketingCompany?.iconURL ?? firstSegment.ownerCompany?.iconURL ?? null,
  };
}

function TripSummarySidebar({
  outboundSummary,
  returnSummary,
  outboundDateLabel,
  returnDateLabel,
  departurePort,
  arrivalPort,
  combinedRoundTripUrl,
}: {
  outboundSummary: TripLegSummary | null;
  returnSummary: TripLegSummary | null;
  outboundDateLabel: string;
  returnDateLabel: string;
  departurePort: string;
  arrivalPort: string;
  combinedRoundTripUrl: string | null;
}) {
  const selectedCount = (outboundSummary ? 1 : 0) + (returnSummary ? 1 : 0);
  const bothSelected = Boolean(outboundSummary && returnSummary);
  const totalCents = bothSelected
    ? (outboundSummary!.priceCents ?? 0) + (returnSummary!.priceCents ?? 0)
    : null;

  const renderRow = (label: string, dateLabel: string, summary: TripLegSummary) => (
    <div key={label} className="flex flex-col gap-2 rounded-lg border border-gray-100 bg-neutral-50 p-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-neutral-500">{label}</span>
        <span className="text-xs text-neutral-500">{dateLabel}</span>
      </div>
      <div className="text-sm font-semibold text-neutral-900">
        {summary.depTime} {summary.depPort} → {summary.arrTime} {summary.arrPort}
      </div>
      <div className="flex items-center gap-2">
        {summary.companyIconURL && (
          <img src={summary.companyIconURL} alt="" className="h-5 w-5 rounded object-contain" />
        )}
        <span className="text-xs text-neutral-600">{summary.companyName}</span>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col gap-4 rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
      <div className="text-base font-bold text-neutral-900">
        {selectedCount} of 2 trips selected
      </div>

      {selectedCount === 0 && (
        <p className="text-sm text-neutral-500">
          Select your outbound and return ferries below to build your round trip.
        </p>
      )}

      <div className="flex flex-col gap-3">
        {outboundSummary && renderRow("Outbound", outboundDateLabel, outboundSummary)}
        {returnSummary && renderRow("Return", returnDateLabel, returnSummary)}
      </div>

      {bothSelected && (
        <div className="flex flex-col gap-3 border-t border-gray-100 pt-4">
          <div>
            <div className="text-xs text-neutral-400">
              Round trip · {departurePort} ↔ {arrivalPort}
            </div>
            <div className="text-xl font-bold text-[#0052ff]">
              Total {formatMoneyFromCents(totalCents)}
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              if (!combinedRoundTripUrl) return;
              window.open(combinedRoundTripUrl, "_blank", "noopener,noreferrer");
            }}
            disabled={!combinedRoundTripUrl}
            className="flex items-center justify-center gap-2 whitespace-nowrap rounded-[12px] bg-[#0052ff] px-6 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Continue to Ferryhopper
            <img src="/OuterLink.svg" alt="" className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}

export default function FerriesPage() {
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      role: "assistant",
      content:
        "Kalimera! 🇬🇷 Where would you like to go today?",
    },
  ]);
  const [input, setInput] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isLoadingTrips, setIsLoadingTrips] = useState(false);

  // Iframe height tracking - send height updates to parent window
  useEffect(() => {
    const sendHeight = () => {
      const height = document.body.scrollHeight;
      window.parent.postMessage({ type: 'resize', height: height }, '*');
    };

    // Send initial height
    sendHeight();

    // Send on window resize
    const handleResize = () => {
      sendHeight();
    };
    window.addEventListener('resize', handleResize);

    // Watch for DOM changes (content updates)
    const observer = new MutationObserver(sendHeight);
    observer.observe(document.body, { 
      childList: true, 
      subtree: true,
      attributes: true,
      characterData: true
    });

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      observer.disconnect();
    };
  }, []);
  const [error, setError] = useState<string | null>(null);
  const [trips, setTrips] = useState<Trip[]>([]);
  const [searchSummary, setSearchSummary] = useState<string | null>(null);
  const [expandedTripIndex, setExpandedTripIndex] = useState<number | null>(
    null
  );
  const [expandedReturnTripIndex, setExpandedReturnTripIndex] = useState<number | null>(
    null
  );
  const [selectedForwardTrip, setSelectedForwardTrip] = useState<Trip | null>(null);
  const [selectedReturnTrip, setSelectedReturnTrip] = useState<Trip | null>(null);
  const [showAllOutboundTrips, setShowAllOutboundTrips] = useState(false);
  const [showAllReturnTrips, setShowAllReturnTrips] = useState(false);
  const [sortBy, setSortBy] = useState<"recommended" | "price" | "duration" | "earliest">("recommended");
  const [returnSortBy, setReturnSortBy] = useState<"recommended" | "price" | "duration" | "earliest">("recommended");
  const [searchParams, setSearchParams] = useState<{
    departurePort: string;
    arrivalPort: string;
    departurePortCode: string;
    arrivalPortCode: string;
    date: string;
    isRoundTrip?: boolean;
    returnDate?: string;
  } | null>(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [showReturnCalendar, setShowReturnCalendar] = useState(false);
  const [returnCalendarMonth, setReturnCalendarMonth] = useState<Date>(new Date());
  const [returnTrips, setReturnTrips] = useState<Trip[]>([]);
  const [isLoadingReturnTrips, setIsLoadingReturnTrips] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState<Date>(new Date());
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [showRecordingModal, setShowRecordingModal] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [audioLevel, setAudioLevel] = useState(0);
  const [liveTranscript, setLiveTranscript] = useState('');
  const [loadingStatus, setLoadingStatus] = useState<string | null>(null);
  const [isEditingPorts, setIsEditingPorts] = useState(true);
  const [editDeparture, setEditDeparture] = useState('');
  const [editArrival, setEditArrival] = useState('');
  const [editDate, setEditDate] = useState(new Date().toISOString().split('T')[0]);
  const [editIsRoundTrip, setEditIsRoundTrip] = useState(false);
  const [editReturnDate, setEditReturnDate] = useState<string | undefined>(undefined);
  const [allPorts, setAllPorts] = useState<Array<{ name: string; code: string }>>([]);
  const [filteredDeparturePorts, setFilteredDeparturePorts] = useState<Array<{ name: string; code: string }>>([]);
  const [filteredArrivalPorts, setFilteredArrivalPorts] = useState<Array<{ name: string; code: string }>>([]);
  const [showDepartureDropdown, setShowDepartureDropdown] = useState(false);
  const [showArrivalDropdown, setShowArrivalDropdown] = useState(false);

  const bottomRef = useRef<HTMLDivElement | null>(null);
  const chatContainerRef = useRef<HTMLDivElement | null>(null);
  const tripsRef = useRef<HTMLDivElement | null>(null);
  const returnTripsRef = useRef<HTMLDivElement | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const recognitionRef = useRef<any>(null);
  const searchAbortControllerRef = useRef<AbortController | null>(null);
  const searchDebounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastSearchParamsRef = useRef<string | null>(null);
  const chatAbortControllerRef = useRef<AbortController | null>(null);

  // Collapse back to the selected-trip view whenever a fresh set of results comes in
  useEffect(() => {
    setShowAllOutboundTrips(false);
  }, [trips]);

  useEffect(() => {
    setShowAllReturnTrips(false);
  }, [returnTrips]);

  // Load persisted data from localStorage on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        const savedMessages = localStorage.getItem('ferry-chat-messages');
        const savedTrips = localStorage.getItem('ferry-trips');
        const savedSearchParams = localStorage.getItem('ferry-search-params');

        if (savedMessages) {
          const parsedMessages = JSON.parse(savedMessages);
          setMessages(parsedMessages);
        }

        if (savedTrips) {
          const parsedTrips = JSON.parse(savedTrips);
          setTrips(parsedTrips);
        }

        if (savedSearchParams) {
          const parsedParams = JSON.parse(savedSearchParams);
          setSearchParams(parsedParams);
        }

        const savedReturnTrips = localStorage.getItem('ferry-return-trips');
        if (savedReturnTrips) {
          const parsedReturnTrips = JSON.parse(savedReturnTrips);
          setReturnTrips(parsedReturnTrips);
        }
      } catch (error) {
        console.error('Error loading saved data:', error);
      }
    }
  }, []);

  // Persist messages to localStorage whenever they change (async to avoid blocking render)
  useEffect(() => {
    if (typeof window !== 'undefined' && messages.length > 0) {
      setTimeout(() => {
        try {
          localStorage.setItem('ferry-chat-messages', JSON.stringify(messages));
        } catch (error) {
          console.error('Error saving messages:', error);
        }
      }, 0);
    }
  }, [messages]);

  // Persist trips to localStorage whenever they change (async to avoid blocking render)
  useEffect(() => {
    if (typeof window !== 'undefined' && trips.length > 0) {
      setTimeout(() => {
        try {
          localStorage.setItem('ferry-trips', JSON.stringify(trips));
        } catch (error) {
          console.error('Error saving trips:', error);
        }
      }, 0);
    }
  }, [trips]);

  // Persist search params to localStorage whenever they change (async to avoid blocking render)
  useEffect(() => {
    console.log('searchParams state changed to:', searchParams);
    if (typeof window !== 'undefined' && searchParams) {
      setTimeout(() => {
        try {
          localStorage.setItem('ferry-search-params', JSON.stringify(searchParams));
          console.log('Saved to localStorage:', searchParams);
        } catch (error) {
          console.error('Error saving search params:', error);
        }
      }, 0);
    }
  }, [searchParams]);

  // Persist return trips to localStorage whenever they change
  useEffect(() => {
    if (typeof window !== 'undefined' && returnTrips.length > 0) {
      setTimeout(() => {
        try {
          localStorage.setItem('ferry-return-trips', JSON.stringify(returnTrips));
        } catch (error) {
          console.error('Error saving return trips:', error);
        }
      }, 0);
    }
  }, [returnTrips]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, trips.length]);

  // Handle URL parameters and postMessage from parent window
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Read URL parameter on load
    const searchParams = new URLSearchParams(window.location.search);
    const searchQuery = searchParams.get('search');
    if (searchQuery) {
      // Trigger search using the chat interface
      sendMessage(searchQuery);
    }

    // Listen for postMessage from parent window
    const handleMessage = (event: MessageEvent) => {
      if (event.origin === 'https://stg.discovergreece.com') {
        if (event.data.type === 'search' && event.data.query) {
          sendMessage(event.data.query);
        }
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []); // Empty dependency array - only run once on mount

  // Close calendar when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (showCalendar && !(event.target as Element).closest('.relative')) {
        setShowCalendar(false);
      }
      if (showReturnCalendar && !(event.target as Element).closest('.relative')) {
        setShowReturnCalendar(false);
      }
      
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCalendar, showReturnCalendar]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Element;
      
      // Check if click is outside departure dropdown
      if (showDepartureDropdown && !target.closest('[data-dropdown="departure"]')) {
        setShowDepartureDropdown(false);
      }
      
      // Check if click is outside arrival dropdown
      if (showArrivalDropdown && !target.closest('[data-dropdown="arrival"]')) {
        setShowArrivalDropdown(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDepartureDropdown, showArrivalDropdown]);

  // Fetch all ports on mount
  useEffect(() => {
    async function fetchPorts() {
      try {
        console.log('[Fetching ports from /api/ferries/ports...]');
        const res = await fetch('/api/ferries/ports');
        console.log('[Ports response status]', res.status, res.statusText);

        if (!res.ok) {
          console.error('[Ports fetch failed]', res.status, res.statusText);
          return;
        }

        const data = await res.json();
        console.log('[Ports response data]', data);

        if (!data) {
          console.error('[Ports data is null/undefined]');
          return;
        }

        if (Array.isArray(data.ports)) {
          console.log(`[Ports loaded successfully] ${data.ports.length} ports available`);
          console.log('[First 5 ports]', data.ports.slice(0, 5));
          setAllPorts(data.ports);
        } else {
          console.error('[Ports data.ports is not an array]', typeof data.ports, data.ports);
        }
      } catch (error) {
        console.error('[Failed to fetch ports - exception]', error);
        if (error instanceof Error) {
          console.error('[Error details]', error.message, error.stack);
        }
      }
    }
    fetchPorts();
  }, []);

  // Sync edit fields with searchParams whenever searchParams changes
  useEffect(() => {
    if (searchParams) {
      setEditDeparture(searchParams.departurePort || '');
      setEditArrival(searchParams.arrivalPort || '');
      setEditDate(searchParams.date || new Date().toISOString().split('T')[0]);
      setEditIsRoundTrip(searchParams.isRoundTrip || false);
      setEditReturnDate(searchParams.returnDate);
    }
  }, [searchParams]);

  // Filter departure ports based on input
  useEffect(() => {
    if (!editDeparture || editDeparture.length < 1) {
      setFilteredDeparturePorts([]);
      return;
    }
    const filtered = allPorts.filter(port =>
      port.name.toLowerCase().includes(editDeparture.toLowerCase())
    ).slice(0, 10);
    console.log('[Departure Filter]', editDeparture, '→', filtered.length, 'results');
    setFilteredDeparturePorts(filtered);
  }, [editDeparture, allPorts]);

  // Filter arrival ports based on input
  useEffect(() => {
    if (!editArrival || editArrival.length < 1) {
      setFilteredArrivalPorts([]);
      return;
    }
    const filtered = allPorts.filter(port =>
      port.name.toLowerCase().includes(editArrival.toLowerCase())
    ).slice(0, 10);
    console.log('[Arrival Filter]', editArrival, '→', filtered.length, 'results');
    setFilteredArrivalPorts(filtered);
  }, [editArrival, allPorts]);

  const suggestions = useMemo(
    () => [
      "Show earliest",
      "Cheapest option",
      "Change date",
    ],
    []
  );

  // Memoize price calculations to avoid O(n²) complexity during sorting
  const tripPriceCache = useMemo(() => {
    const cache = new Map<Trip, number>();
    trips.forEach(trip => {
      cache.set(trip, minAccommodationPriceCents(trip) ?? Infinity);
    });
    return cache;
  }, [trips]);

  // Sort and filter trips
  const sortedTrips = useMemo(() => {
    const tripsCopy = [...trips];

    if (sortBy === "price") {
      return tripsCopy.sort((a, b) => {
        const priceA = tripPriceCache.get(a) ?? Infinity;
        const priceB = tripPriceCache.get(b) ?? Infinity;
        return priceA - priceB;
      });
    } else if (sortBy === "duration") {
      return tripsCopy.sort((a, b) => {
        const segmentsA = (a as { segments?: Array<Record<string, unknown>> }).segments ?? [];
        const segmentsB = (b as { segments?: Array<Record<string, unknown>> }).segments ?? [];
        const firstSegmentA = segmentsA[0] as { departureDateTime?: string; arrivalDateTime?: string } | undefined;
        const firstSegmentB = segmentsB[0] as { departureDateTime?: string; arrivalDateTime?: string } | undefined;

        const durationA = firstSegmentA?.departureDateTime && firstSegmentA?.arrivalDateTime
          ? new Date(firstSegmentA.arrivalDateTime).getTime() - new Date(firstSegmentA.departureDateTime).getTime()
          : Infinity;
        const durationB = firstSegmentB?.departureDateTime && firstSegmentB?.arrivalDateTime
          ? new Date(firstSegmentB.arrivalDateTime).getTime() - new Date(firstSegmentB.departureDateTime).getTime()
          : Infinity;

        return durationA - durationB;
      });
    } else if (sortBy === "earliest") {
      return tripsCopy.sort((a, b) => {
        const segmentsA = (a as { segments?: Array<Record<string, unknown>> }).segments ?? [];
        const segmentsB = (b as { segments?: Array<Record<string, unknown>> }).segments ?? [];
        const firstSegmentA = segmentsA[0] as { departureDateTime?: string } | undefined;
        const firstSegmentB = segmentsB[0] as { departureDateTime?: string } | undefined;

        const timeA = firstSegmentA?.departureDateTime ? new Date(firstSegmentA.departureDateTime).getTime() : Infinity;
        const timeB = firstSegmentB?.departureDateTime ? new Date(firstSegmentB.departureDateTime).getTime() : Infinity;

        return timeA - timeB;
      });
    }

    return tripsCopy; // recommended (original order)
  }, [trips, sortBy, tripPriceCache]);

  // Memoize price calculations to avoid O(n²) complexity during sorting
  const returnTripPriceCache = useMemo(() => {
    const cache = new Map<Trip, number>();
    returnTrips.forEach(trip => {
      cache.set(trip, minAccommodationPriceCents(trip) ?? Infinity);
    });
    return cache;
  }, [returnTrips]);

  // Sort and filter return trips
  const sortedReturnTrips = useMemo(() => {
    const tripsCopy = [...returnTrips];

    if (returnSortBy === "price") {
      return tripsCopy.sort((a, b) => {
        const priceA = returnTripPriceCache.get(a) ?? Infinity;
        const priceB = returnTripPriceCache.get(b) ?? Infinity;
        return priceA - priceB;
      });
    } else if (returnSortBy === "duration") {
      return tripsCopy.sort((a, b) => {
        const segmentsA = (a as { segments?: Array<Record<string, unknown>> }).segments ?? [];
        const segmentsB = (b as { segments?: Array<Record<string, unknown>> }).segments ?? [];
        const firstSegmentA = segmentsA[0] as { departureDateTime?: string; arrivalDateTime?: string } | undefined;
        const firstSegmentB = segmentsB[0] as { departureDateTime?: string; arrivalDateTime?: string } | undefined;

        const durationA = firstSegmentA?.departureDateTime && firstSegmentA?.arrivalDateTime
          ? new Date(firstSegmentA.arrivalDateTime).getTime() - new Date(firstSegmentA.departureDateTime).getTime()
          : Infinity;
        const durationB = firstSegmentB?.departureDateTime && firstSegmentB?.arrivalDateTime
          ? new Date(firstSegmentB.arrivalDateTime).getTime() - new Date(firstSegmentB.departureDateTime).getTime()
          : Infinity;

        return durationA - durationB;
      });
    } else if (returnSortBy === "earliest") {
      return tripsCopy.sort((a, b) => {
        const segmentsA = (a as { segments?: Array<Record<string, unknown>> }).segments ?? [];
        const segmentsB = (b as { segments?: Array<Record<string, unknown>> }).segments ?? [];
        const firstSegmentA = segmentsA[0] as { departureDateTime?: string } | undefined;
        const firstSegmentB = segmentsB[0] as { departureDateTime?: string } | undefined;

        const timeA = firstSegmentA?.departureDateTime ? new Date(firstSegmentA.departureDateTime).getTime() : Infinity;
        const timeB = firstSegmentB?.departureDateTime ? new Date(firstSegmentB.departureDateTime).getTime() : Infinity;

        return timeA - timeB;
      });
    }

    return tripsCopy; // recommended (original order)
  }, [returnTrips, returnSortBy, returnTripPriceCache]);

  function handleFilterClick(filter: string) {
    if (filter === "Cheapest option") {
      setSortBy("price");
      // Scroll to trips section
      setTimeout(() => {
        tripsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } else if (filter === "Show earliest") {
      setSortBy("earliest");
      // Scroll to trips section
      setTimeout(() => {
        tripsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } else if (filter === "Change date") {
      // Toggle calendar visibility
      setShowCalendar(!showCalendar);
    }
  }

  function showInfoMessage(message: string) {
    setInfoMessage(message);
    setTimeout(() => setInfoMessage(null), 3000);
  }

  function scrollToChat() {
    setTimeout(() => {
      chatContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }

  async function performSearch(params: typeof searchParams) {
    if (!params) return;

    // Validate that port codes are defined
    if (!params.departurePortCode || !params.arrivalPortCode) {
      console.log('[Search] Skipping search - port codes not defined:', {
        departurePortCode: params.departurePortCode,
        arrivalPortCode: params.arrivalPortCode
      });
      return;
    }

    // Optimization 4: Request deduplication
    const paramsKey = JSON.stringify(params);
    if (paramsKey === lastSearchParamsRef.current) {
      console.log('[Dedup] Skipping duplicate request');
      return;
    }
    lastSearchParamsRef.current = paramsKey;

    // Optimization 4: Cancel previous request if still in progress
    if (searchAbortControllerRef.current) {
      searchAbortControllerRef.current.abort();
      console.log('[Abort] Cancelled previous search request');
    }

    const abortController = new AbortController();
    searchAbortControllerRef.current = abortController;

    setIsLoadingTrips(true);
    if (params.isRoundTrip && params.returnDate) {
      setIsLoadingReturnTrips(true);
    }
    setError(null);

    try {
      console.log('Calling search API with params:', {
        departurePortCode: params.departurePortCode,
        arrivalPortCode: params.arrivalPortCode,
        departurePort: params.departurePort,
        arrivalPort: params.arrivalPort,
        date: params.date,
        isRoundTrip: params.isRoundTrip,
        returnDate: params.returnDate,
      });

      const response = await fetch('/api/ferries/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          departurePortCode: params.departurePortCode,
          arrivalPortCode: params.arrivalPortCode,
          departurePort: params.departurePort,
          arrivalPort: params.arrivalPort,
          date: params.date,
        }),
        signal: abortController.signal, // Add abort signal
      });

      // Check if request was aborted
      if (abortController.signal.aborted) {
        console.log('[Abort] Request was cancelled');
        return;
      }

      const data = await response.json();

      console.log('Search API response status:', response.status);
      console.log('Search API response:', data);
      console.log('Response type:', Array.isArray(data) ? 'array' : typeof data);
      console.log('Requested date:', params.date);

      // Handle both response formats: array of trips or {trips: [...]}
      const tripsArray = Array.isArray(data) ? data : data.trips;

      if (tripsArray && Array.isArray(tripsArray)) {
        console.log('Setting trips:', tripsArray.length, 'trips found');

        // Check what date the returned trips actually have
        if (tripsArray.length > 0 && tripsArray[0].segments?.[0]?.departureDateTime) {
          const firstTripDate = tripsArray[0].segments[0].departureDateTime.substring(0, 10);
          console.log('First trip date from API:', firstTripDate);
          console.log('Date we requested:', params.date);
          if (firstTripDate !== params.date) {
            console.warn('WARNING: API returned trips for different date!');
          }
        }

        setTrips(tripsArray);
        setSelectedForwardTrip(null);
        console.log('setTrips called with', tripsArray.length, 'trips');

        // No results for this route/date — bring the chat back into view so the
        // user sees the search form and can adjust their query.
        if (tripsArray.length === 0) {
          scrollToChat();
        }

        // NOTE: We don't update searchParams here anymore - it should be updated
        // by the calling function (handleDateChange, handleSelectDate, etc.) BEFORE calling performSearch

        // If round trip, search for return journey
        if (params.isRoundTrip && params.returnDate) {
          performReturnSearch(params);
        }

      } else if (data.error && data.matches && Array.isArray(data.matches)) {
        // Handle ambiguous port error - automatically pick the first match and retry
        console.log('Port ambiguity detected, auto-selecting first match:', data.matches[0]);

        let updatedParams = { ...params };

        // Determine which port was ambiguous and update the code
        if (data.error.includes('Departure port')) {
          updatedParams.departurePortCode = data.matches[0];
        } else if (data.error.includes('Arrival port')) {
          updatedParams.arrivalPortCode = data.matches[0];
        }

        console.log('Retrying with resolved port code:', updatedParams);

        // Retry the search with the resolved port code
        const retryResponse = await fetch('/api/ferries/search', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            departurePortCode: updatedParams.departurePortCode,
            arrivalPortCode: updatedParams.arrivalPortCode,
            departurePort: updatedParams.departurePort,
            arrivalPort: updatedParams.arrivalPort,
            date: updatedParams.date,
          }),
        });

        const retryData = await retryResponse.json();

        if (retryData.trips) {
          console.log('Retry successful, setting trips:', retryData.trips.length);
          setTrips(retryData.trips);
          setSelectedForwardTrip(null);
          setSearchParams(updatedParams);
          if (retryData.trips.length === 0) {
            scrollToChat();
          }
        } else if (retryData.error && retryData.matches && Array.isArray(retryData.matches)) {
          // Second ambiguity (e.g., departure port after resolving arrival port)
          console.log('Second port ambiguity detected, auto-selecting:', retryData.matches[0]);

          if (retryData.error.includes('Departure port')) {
            updatedParams.departurePortCode = retryData.matches[0];
          } else if (retryData.error.includes('Arrival port')) {
            updatedParams.arrivalPortCode = retryData.matches[0];
          }

          console.log('Retrying again with both ports resolved:', updatedParams);

          // Second retry with both ports resolved
          const retry2Response = await fetch('/api/ferries/search', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              departurePortCode: updatedParams.departurePortCode,
              arrivalPortCode: updatedParams.arrivalPortCode,
              departurePort: updatedParams.departurePort,
              arrivalPort: updatedParams.arrivalPort,
              date: updatedParams.date,
            }),
          });

          const retry2Data = await retry2Response.json();

          if (retry2Data.trips) {
            console.log('Second retry successful, setting trips:', retry2Data.trips.length);
            setTrips(retry2Data.trips);
            setSelectedForwardTrip(null);
            setSearchParams(updatedParams);
            if (retry2Data.trips.length === 0) {
              scrollToChat();
            }
          } else if (retry2Data.error) {
            console.error('Second retry failed:', retry2Data.error);
            setError(retry2Data.error);
            scrollToChat();
          }
        } else if (retryData.error) {
          console.error('Retry failed:', retryData.error);
          setError(retryData.error);
          scrollToChat();
        }
      } else if (data.error) {
        console.error('Search API error:', data.error);
        setError(data.error);
        scrollToChat();
      }
    } catch (error) {
      // Don't show error if request was aborted
      if (error instanceof Error && error.name === 'AbortError') {
        console.log('[Abort] Search request was cancelled');
        return;
      }
      setError('Failed to search trips');
      console.error('Search error:', error);
      scrollToChat();
    } finally {
      // Don't update loading state if request was aborted
      if (!abortController.signal.aborted) {
        setIsLoadingTrips(false);
      }
    }
  }

  async function performReturnSearch(params: typeof searchParams) {
    if (!params || !params.isRoundTrip || !params.returnDate) {
      console.log('[Return Search] Skipping - missing params:', { params });
      return;
    }

    // Validate that port codes are defined
    if (!params.departurePortCode || !params.arrivalPortCode) {
      console.log('[Return Search] Skipping return search - port codes not defined:', {
        departurePortCode: params.departurePortCode,
        arrivalPortCode: params.arrivalPortCode
      });
      setIsLoadingReturnTrips(false);
      return;
    }

    setIsLoadingReturnTrips(true);
    console.log('[Return Search] Starting return trip search...');

    try {
      console.log('Calling return search API with params:', {
        departurePortCode: params.arrivalPortCode, // Swapped for return journey
        arrivalPortCode: params.departurePortCode, // Swapped for return journey
        departurePort: params.arrivalPort,
        arrivalPort: params.departurePort,
        date: params.returnDate,
      });

      const response = await fetch('/api/ferries/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          departurePortCode: params.arrivalPortCode, // Swapped for return journey
          arrivalPortCode: params.departurePortCode, // Swapped for return journey
          departurePort: params.arrivalPort,
          arrivalPort: params.departurePort,
          date: params.returnDate,
        }),
      });

      const data = await response.json();
      console.log('Return search API response:', data);
      const tripsArray = Array.isArray(data) ? data : data.trips;

      if (tripsArray && Array.isArray(tripsArray)) {
        console.log('Setting return trips:', tripsArray.length, 'trips found');
        setReturnTrips(tripsArray);
        setSelectedReturnTrip(null);
      } else if (data.error && data.matches && Array.isArray(data.matches)) {
        // Handle ambiguous port error for return trip
        console.log('Return trip port ambiguity detected, auto-selecting first match:', data.matches[0]);
        
        // Retry with resolved port code
        const retryResponse = await fetch('/api/ferries/search', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            departurePortCode: data.error.includes('Departure port') ? data.matches[0] : params.arrivalPortCode,
            arrivalPortCode: data.error.includes('Arrival port') ? data.matches[0] : params.departurePortCode,
            departurePort: params.arrivalPort,
            arrivalPort: params.departurePort,
            date: params.returnDate,
          }),
        });
        
        const retryData = await retryResponse.json();
        const retryTripsArray = Array.isArray(retryData) ? retryData : retryData.trips;
        
        if (retryTripsArray && Array.isArray(retryTripsArray)) {
          console.log('Return trip retry successful:', retryTripsArray.length, 'trips found');
          setReturnTrips(retryTripsArray);
          setSelectedReturnTrip(null);
        } else if (retryData.error) {
          console.error('Return trip retry failed:', retryData.error);
        }
      } else if (data.error) {
        console.error('Return search API error:', data.error);
      }
    } catch (error) {
      console.error('Return search error:', error);
    } finally {
      setIsLoadingReturnTrips(false);
    }
  }

  // Optimization 4: Date navigation with debouncing and cancellation
  function handleDateChange(dayDelta: number) {
    if (!searchParams) return;

    console.log('handleDateChange called with delta:', dayDelta);
    console.log('Current searchParams:', searchParams);

    // Parse current date without timezone issues
    const [year, month, day] = searchParams.date.split('-').map(Number);
    const currentDate = new Date(year, month - 1, day);

    // Add the day delta
    currentDate.setDate(currentDate.getDate() + dayDelta);

    // Format back to YYYY-MM-DD without timezone conversion
    const newYear = currentDate.getFullYear();
    const newMonth = String(currentDate.getMonth() + 1).padStart(2, '0');
    const newDay = String(currentDate.getDate()).padStart(2, '0');
    const newDate = `${newYear}-${newMonth}-${newDay}`;

    console.log('New date:', newDate);

    // Create completely new params object - preserve round trip settings
    const updatedParams = {
      departurePort: searchParams.departurePort,
      arrivalPort: searchParams.arrivalPort,
      departurePortCode: searchParams.departurePortCode,
      arrivalPortCode: searchParams.arrivalPortCode,
      date: newDate,
      isRoundTrip: searchParams.isRoundTrip,
      returnDate: searchParams.returnDate,
    };

    console.log('About to call setSearchParams with:', updatedParams);

    // Update state FIRST
    setSearchParams(updatedParams);

    console.log('setSearchParams called from handleDateChange');

    // Cancel previous search if in progress
    if (searchAbortControllerRef.current) {
      searchAbortControllerRef.current.abort();
      console.log('[Debounce] Aborted previous search');
    }

    // Debounce rapid clicks (300ms)
    if (searchDebounceTimerRef.current) {
      clearTimeout(searchDebounceTimerRef.current);
    }

    searchDebounceTimerRef.current = setTimeout(() => {
      performSearch(updatedParams);
    }, 300);
  }

  function handleSelectDate(dateString: string) {
    if (!searchParams) return;

    console.log('handleSelectDate called with date:', dateString);
    console.log('Current searchParams:', searchParams);

    // Create completely new params object - preserve round trip settings
    const updatedParams = {
      departurePort: searchParams.departurePort,
      arrivalPort: searchParams.arrivalPort,
      departurePortCode: searchParams.departurePortCode,
      arrivalPortCode: searchParams.arrivalPortCode,
      date: dateString,
      isRoundTrip: searchParams.isRoundTrip,
      returnDate: searchParams.returnDate,
    };

    console.log('About to call setSearchParams with:', updatedParams);

    setShowCalendar(false);

    // Update state only - search will be triggered by Search button
    setSearchParams(updatedParams);

    console.log('setSearchParams called from handleSelectDate');
  }

  function handlePreviousMonth() {
    setCalendarMonth(prev => {
      const newMonth = new Date(prev);
      newMonth.setMonth(newMonth.getMonth() - 1);
      return newMonth;
    });
  }

  function handleNextMonth() {
    setCalendarMonth(prev => {
      const newMonth = new Date(prev);
      newMonth.setMonth(newMonth.getMonth() + 1);
      return newMonth;
    });
  }

  function handleSelectReturnDate(dateString: string) {
    if (!searchParams) return;

    console.log('handleSelectReturnDate called with date:', dateString);

    const updatedParams = {
      ...searchParams,
      returnDate: dateString,
      isRoundTrip: true,
    };

    setShowReturnCalendar(false);
    setSearchParams(updatedParams);

    // Update state only - search will be triggered by Search button
  }

  function handleReturnDateChange(dayDelta: number) {
    if (!searchParams || !searchParams.returnDate) return;

    console.log('handleReturnDateChange called with delta:', dayDelta);

    // Parse current return date
    const [year, month, day] = searchParams.returnDate.split('-').map(Number);
    const currentDate = new Date(year, month - 1, day);

    // Add the day delta
    currentDate.setDate(currentDate.getDate() + dayDelta);

    // Format back to YYYY-MM-DD
    const newYear = currentDate.getFullYear();
    const newMonth = String(currentDate.getMonth() + 1).padStart(2, '0');
    const newDay = String(currentDate.getDate()).padStart(2, '0');
    const newReturnDate = `${newYear}-${newMonth}-${newDay}`;

    console.log('New return date:', newReturnDate);

    // Ensure return date is after departure date
    if (newReturnDate <= searchParams.date) {
      console.log('Return date must be after departure date');
      return;
    }

    // Update state
    const updatedParams = {
      ...searchParams,
      returnDate: newReturnDate,
    };

    setSearchParams(updatedParams);

    // Trigger return search
    performReturnSearch(updatedParams);
  }

  function handlePreviousReturnMonth() {
    setReturnCalendarMonth(prev => {
      const newMonth = new Date(prev);
      newMonth.setMonth(newMonth.getMonth() - 1);
      return newMonth;
    });
  }

  function handleNextReturnMonth() {
    setReturnCalendarMonth(prev => {
      const newMonth = new Date(prev);
      newMonth.setMonth(newMonth.getMonth() + 1);
      return newMonth;
    });
  }

  function handleToggleRoundTrip() {
    if (!searchParams) return;

    setSelectedForwardTrip(null);
    setSelectedReturnTrip(null);

    const isTogglingOn = !searchParams.isRoundTrip;

    const updatedParams = {
      ...searchParams,
      isRoundTrip: isTogglingOn,
      returnDate: isTogglingOn ? searchParams.returnDate || undefined : undefined,
    };

    setSearchParams(updatedParams);

    // If toggling off, clear return trips
    if (!isTogglingOn) {
      setReturnTrips([]);
    } else if (updatedParams.returnDate) {
      // If toggling on and return date already exists, search for return trips
      performSearch(updatedParams);
    }
  }

  function handleEditPorts() {
    if (!searchParams) return;
    console.log('[Edit Ports] Opening edit mode', searchParams);
    setEditDeparture(searchParams.departurePort);
    setEditArrival(searchParams.arrivalPort);
    setEditDate(searchParams.date);
    setEditIsRoundTrip(searchParams.isRoundTrip || false);
    setEditReturnDate(searchParams.returnDate);
    setIsEditingPorts(true);
  }

  function handleSelectDeparturePort(port: { name: string; code: string }) {
    console.log('[Select Departure Port]', port);
    setEditDeparture(port.name);
    setShowDepartureDropdown(false);

    // Create or update search params
    const updatedParams = searchParams ? {
      ...searchParams,
      departurePort: port.name,
      departurePortCode: port.code,
    } : {
      departurePort: port.name,
      departurePortCode: port.code,
      arrivalPort: editArrival || '',
      arrivalPortCode: '',
      date: new Date().toISOString().split('T')[0],
    };
    console.log('[Updated Params]', updatedParams);
    setSearchParams(updatedParams);
  }

  function handleSelectArrivalPort(port: { name: string; code: string }) {
    console.log('[Select Arrival Port]', port);
    setEditArrival(port.name);
    setShowArrivalDropdown(false);

    // Create or update search params
    const updatedParams = searchParams ? {
      ...searchParams,
      arrivalPort: port.name,
      arrivalPortCode: port.code,
    } : {
      departurePort: editDeparture || '',
      departurePortCode: '',
      arrivalPort: port.name,
      arrivalPortCode: port.code,
      date: new Date().toISOString().split('T')[0],
    };
    console.log('[Updated Params]', updatedParams);
    setSearchParams(updatedParams);
  }

  function handleCancelEditPorts() {
    console.log('[Cancel Edit Ports]');
    setIsEditingPorts(false);
    setEditDeparture('');
    setEditArrival('');
    setEditDate('');
    setEditIsRoundTrip(false);
    setEditReturnDate(undefined);
    setShowDepartureDropdown(false);
    setShowArrivalDropdown(false);
  }

  function handleApplyPortChanges() {
    console.log('[Apply Port Changes] Current searchParams:', searchParams);

    // For initial search, searchParams might be null, so we create it from edit fields
    const params = searchParams || {
      departurePort: '',
      arrivalPort: '',
      departurePortCode: '',
      arrivalPortCode: '',
      date: editDate || new Date().toISOString().split('T')[0],
      isRoundTrip: editIsRoundTrip,
      returnDate: editReturnDate,
    };

    // If editing existing search, update all fields
    if (searchParams) {
      params.date = editDate || searchParams.date;
      params.isRoundTrip = editIsRoundTrip;
      params.returnDate = editReturnDate;
    }

    // Validate that both ports have codes
    if (!params.departurePortCode || !params.arrivalPortCode) {
      console.error('[Apply Port Changes] Missing port codes!', params);
      alert('Please select both departure and arrival ports from the dropdown');
      return;
    }

    console.log('[Apply Port Changes] Triggering search with:', params);
    // Don't reset form - keep it populated for easy editing
    setSearchParams(params); // Update searchParams state
    performSearch(params);
    
    // If round trip, also search for return trips
    if (params.isRoundTrip && params.returnDate) {
      performReturnSearch(params);
    }
  }

  async function handleUpdateSearch() {
    performSearch(searchParams);
  }

  async function startRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      // Setup audio level detection
      const audioContext = new AudioContext();
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;

      // Monitor audio levels
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      const monitorAudioLevel = () => {
        if (!analyserRef.current) return;

        analyserRef.current.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
        const normalizedLevel = Math.min(100, (average / 128) * 100);
        setAudioLevel(normalizedLevel);

        animationFrameRef.current = requestAnimationFrame(monitorAudioLevel);
      };
      monitorAudioLevel();

      // Try to start live transcription (optional - will fail silently if network issues)
      setLiveTranscript('');
      const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      if (SpeechRecognition) {
        try {
          const recognition = new SpeechRecognition();
          recognition.continuous = true;
          recognition.interimResults = true;
          recognition.lang = 'en-US';

          recognition.onresult = (event: any) => {
            let transcript = '';
            for (let i = 0; i < event.results.length; i++) {
              transcript += event.results[i][0].transcript;
              if (i < event.results.length - 1) transcript += ' ';
            }
            setLiveTranscript(transcript);
          };

          recognition.onerror = (event: any) => {
            console.log('Live transcription unavailable:', event.error);
            // Silently fail - audio bars will still work
          };

          recognitionRef.current = recognition;
          recognition.start();
        } catch (err) {
          console.log('Live transcription not available');
          // Continue without live transcription
        }
      }

      // Start duration timer
      setRecordingDuration(0);
      recordingIntervalRef.current = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);

      mediaRecorder.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = async () => {
        // Stop live transcription
        if (recognitionRef.current) {
          try {
            recognitionRef.current.stop();
          } catch (err) {
            // Already stopped
          }
          recognitionRef.current = null;
        }

        // Stop audio monitoring
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }

        // Close audio context
        if (audioContextRef.current) {
          audioContextRef.current.close();
          audioContextRef.current = null;
        }

        analyserRef.current = null;
        setAudioLevel(0);
        setLiveTranscript('');

        // Stop duration timer
        if (recordingIntervalRef.current) {
          clearInterval(recordingIntervalRef.current);
          recordingIntervalRef.current = null;
        }

        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const formData = new FormData();
        formData.append('audio', audioBlob);

        setIsRecording(false);
        setShowRecordingModal(false);
        showInfoMessage('Processing your voice...');

        try {
          const response = await fetch('/api/transcribe', {
            method: 'POST',
            body: formData,
          });

          const data = await response.json();
          if (data.transcript) {
            setInput(data.transcript);
            showInfoMessage(`Heard: "${data.transcript}"`);
          } else {
            showInfoMessage('Failed to transcribe audio');
          }
        } catch (error) {
          console.error('Transcription error:', error);
          showInfoMessage('Failed to transcribe audio');
        }
      };

      setIsRecording(true);
      setShowRecordingModal(true);
      mediaRecorder.start();
    } catch (error) {
      console.error('Error accessing microphone:', error);
      setIsRecording(false);
      showInfoMessage('Microphone access denied. Please enable microphone permissions.');
    }
  }

  function stopRecording() {
    // Stop live transcription
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (err) {
        // Already stopped
      }
      recognitionRef.current = null;
    }

    // Stop audio monitoring
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    // Close audio context
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    analyserRef.current = null;
    setAudioLevel(0);
    setLiveTranscript('');

    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
    }

    // Stop duration timer
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
      recordingIntervalRef.current = null;
    }
  }

  function stopChatResponse() {
    if (chatAbortControllerRef.current) {
      chatAbortControllerRef.current.abort();
      chatAbortControllerRef.current = null;
      setIsSending(false);
      setIsLoadingTrips(false);
    }
  }

  async function sendMessage(text: string) {
    const trimmed = text.trim();
    if (!trimmed || isSending) return;

    setIsSending(true);
    // Only show loading if we don't have trips yet (avoid hiding existing trips on follow-up questions)
    if (trips.length === 0) {
      setIsLoadingTrips(true);
    }
    setError(null);

    const nextMessages: ChatMessage[] = [
      ...messages,
      { role: "user", content: trimmed },
    ];
    setMessages(nextMessages);
    setInput("");

    // Create new abort controller for this request
    chatAbortControllerRef.current = new AbortController();

    try {
      console.log('[ChatGPT] Sending fetch request to /api/ferries/chat');
      const res = await fetch("/api/ferries/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          messages: nextMessages,
          currentSearchParams: searchParams // Pass current form values as context
        }),
        signal: chatAbortControllerRef.current.signal,
      });

      console.log('[ChatGPT] Response received, status:', res.status, 'ok:', res.ok);

      if (!res.ok) {
        const data = await res.json();
        console.error('[ChatGPT] Error response:', data);
        const errorMsg = typeof data?.error === "string" ? data.error : "Chat failed.";
        setError(errorMsg);
        setIsLoadingTrips(false);
        setIsSending(false);
        return;
      }

      // Handle streaming response
      const reader = res.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        console.error('[ChatGPT] No reader available');
        setError("Failed to read response");
        setIsLoadingTrips(false);
        setIsSending(false);
        return;
      }

      console.log('[ChatGPT] Starting to read stream...');
      let buffer = '';
      let chunkCount = 0;
      while (true) {
        const { done, value } = await reader.read();
        chunkCount++;
        console.log('[ChatGPT] Chunk', chunkCount, 'received, done:', done, 'bytes:', value?.length || 0);
        
        if (done) {
          console.log('[ChatGPT] Stream complete after', chunkCount, 'chunks');
          break;
        }

        // Append to buffer instead of processing immediately
        buffer += decoder.decode(value, { stream: true });

        // Split by double newline to get complete SSE messages
        const parts = buffer.split('\n\n');

        // Keep the last part in buffer (might be incomplete)
        buffer = parts.pop() || '';

        // Process complete messages
        for (const part of parts) {
          const lines = part.split('\n');
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                console.log('[ChatGPT] Parsed message type:', data.type, 'data:', data);

            if (data.type === 'status') {
              console.log('[ChatGPT] Status update:', data.data.message);
              setLoadingStatus(data.data.message);
            }
            else if (data.type === 'trips') {
              console.log('[ChatGPT] Trips received, count:', data.data?.trips?.length || 0);
              // Display trips immediately (before LLM summary)
              if (Array.isArray(data.data?.trips)) {
                setTrips(data.data.trips);
                setExpandedTripIndex(null);
                setSelectedForwardTrip(null);

                // Use searchParams from API if available
                if (data.data.searchParams) {
                  console.log('[Chat Response] Received searchParams:', data.data.searchParams);
                  
                  // API now sends full port names along with codes
                  const params = {
                    departurePort: data.data.searchParams.departurePort || data.data.searchParams.departurePortCode,
                    arrivalPort: data.data.searchParams.arrivalPort || data.data.searchParams.arrivalPortCode,
                    departurePortCode: data.data.searchParams.departurePortCode,
                    arrivalPortCode: data.data.searchParams.arrivalPortCode,
                    date: data.data.searchParams.date,
                    isRoundTrip: data.data.searchParams.isRoundTrip || false,
                    returnDate: data.data.searchParams.returnDate,
                  };
                  
                  console.log('[Chat Response] Setting searchParams:', params);
                  setSearchParams(params);

                  // Don't trigger return search here - wait for return_trips event from chat
                  // The chat API will send return trips automatically if it's a round trip
                  if (params.isRoundTrip && params.returnDate) {
                    setReturnTrips([]);
                    setSelectedReturnTrip(null);
                    setIsLoadingReturnTrips(true);
                  }
                } else {
                  console.log('[ChatGPT] No searchParams in response, extracting from trips');
                  // Fallback: Extract search params from trips
                  const params = extractSearchParamsFromTrips(data.data.trips);
                  if (params) {
                    setSearchParams(params);
                  }
                }

                // Extract search summary from first trip
                if (data.data.trips.length > 0) {
                  const firstTrip = data.data.trips[0];
                  const segments = (firstTrip as { segments?: Array<Record<string, unknown>> }).segments ?? [];
                  const firstSegment = segments[0] as {
                    departurePort?: { name?: string };
                    arrivalPort?: { name?: string };
                    departureDateTime?: string;
                  } | undefined;

                  if (firstSegment?.departurePort?.name && firstSegment?.arrivalPort?.name) {
                    const from = firstSegment.departurePort.name;
                    const to = firstSegment.arrivalPort.name;
                    const dateStr = firstSegment.departureDateTime
                      ? new Date(firstSegment.departureDateTime).toLocaleDateString('en-US', {
                          weekday: 'short',
                          day: 'numeric',
                          month: 'short'
                        })
                      : '';

                    setSearchSummary(`${from} → ${to}${dateStr ? ` • ${dateStr}` : ''}`);
                  }
                } else {
                  setSearchSummary(null);
                }

                // Invalid/no-result searches send an empty trips array (a real search
                // always finds at least one trip by the time this event fires) — bring
                // the chat back into view so the user sees why nothing showed up.
                if (data.data.trips.length === 0) {
                  scrollToChat();
                }

                setIsLoadingTrips(false);
              }
            }
            else if (data.type === 'return_trips') {
              console.log('[ChatGPT] Return trips received, count:', data.data?.trips?.length || 0);
              // Handle return trips from chat (no need to call performReturnSearch)
              if (Array.isArray(data.data?.trips)) {
                setReturnTrips(data.data.trips);
                setSelectedReturnTrip(null);
                setIsLoadingReturnTrips(false);
              }
            }
            else if (data.type === 'assistant_message') {
              console.log('[ChatGPT] Assistant message received, length:', data.data?.message?.length || 0);
              // Add assistant message to chat (after trips are already displayed)
              if (typeof data.data?.message === "string") {
                setMessages((prev) => [
                  ...prev,
                  { role: "assistant", content: data.data.message },
                ]);
              }
            }
            else if (data.type === 'complete') {
              console.log('[ChatGPT] Stream marked as complete');
              // Final cleanup
              setLoadingStatus(null);
              setIsSending(false);
            }
            else if (data.type === 'error') {
              console.error('[ChatGPT] Error message received:', data.data.message);
              setError(data.data.message || "An error occurred");
              scrollToChat();
              setIsLoadingTrips(false);
              setLoadingStatus(null);
              setIsSending(false);
            }
            else {
              console.warn('[ChatGPT] Unknown message type:', data.type);
            }
              } catch (parseError) {
                console.error("[ChatGPT] JSON parse error:", parseError);
                console.error("[ChatGPT] Failed to parse line:", line);
              }
            }
          }
        }
      }
      
      console.log('[ChatGPT] Finished processing all chunks');
      
    } catch (err) {
      // Check if this was an abort (user clicked stop)
      if (err instanceof Error && err.name === 'AbortError') {
        console.log("[ChatGPT] Request was cancelled by user");
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: "Request cancelled." },
        ]);
      } else {
        console.error("[ChatGPT] Unexpected error:", err);
        setError("Unable to connect. Please check your internet connection and try again.");
      }
      setIsLoadingTrips(false);
      setLoadingStatus(null);
      setIsSending(false);
    } finally {
      console.log('[ChatGPT] Cleanup - setting abort controller to null');
      chatAbortControllerRef.current = null;
    }
  }

  const bothTripsSelected = Boolean(selectedForwardTrip && selectedReturnTrip);
  const roundTripStep: 1 | 2 | 3 = bothTripsSelected ? 3 : selectedForwardTrip ? 2 : 1;
  const outboundIDSCode = getIDSCodeFromDeepLink((selectedForwardTrip as { deepLink?: string } | null)?.deepLink);
  const returnIDSCode = getIDSCodeFromDeepLink((selectedReturnTrip as { deepLink?: string } | null)?.deepLink);
  const combinedRoundTripUrl =
    searchParams?.departurePortCode && searchParams?.arrivalPortCode && searchParams?.date && searchParams?.returnDate && outboundIDSCode && returnIDSCode
      ? buildRoundTripUrl(
          searchParams.departurePortCode,
          searchParams.arrivalPortCode,
          searchParams.date,
          searchParams.returnDate,
          outboundIDSCode,
          returnIDSCode
        )
      : null;
  const isRoundTripSearch = Boolean(searchParams?.isRoundTrip && searchParams.returnDate);
  const outboundCollapsed = isRoundTripSearch && roundTripStep >= 2 && !showAllOutboundTrips;
  const returnCollapsed = isRoundTripSearch && roundTripStep >= 3 && !showAllReturnTrips;

  return (
    <div className="min-h-screen bg-white">
      {/* Info Message Toast */}
      {infoMessage && (
        <div className="fixed right-4 top-4 z-50 rounded-lg bg-blue-500 px-4 py-3 text-white shadow-lg">
          {infoMessage}
        </div>
      )}

      <div className="mx-auto max-w-6xl px-4 py-8 md:px-6 lg:px-12">
        {/* Header */}
        {/* Chat Interface */}
        <div ref={chatContainerRef} className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
          <div className="max-h-[500px] space-y-6 overflow-y-auto scroll-smooth p-4 pb-4 md:p-6">
            {messages.map((m, idx) => (
              <div
                key={`${m.role}-${idx}`}
                className={`flex items-start gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}
              >
                {/* Avatar */}
                <div
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
                    m.role === "user"
                      ? "text-white"
                      : "bg-[#FFFFFF] text-white border border-gray-200"
                  }`}
                  style={m.role === "user" ? { backgroundColor: '#0052ff' } : undefined}
                >
                  {m.role === "user" ? (
                    <span className="text-xs font-bold">ME</span>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 1049.99 1049.99">
                      <path fill="#0052fe" d="M350.61,699.38l210-90-120-120-90,210Z"/>
                      <path fill="#0052fe" d="M774.37,0h-498.75C123.74,0,0,123.75,0,275.62v498.75c0,151.88,123.75,275.62,275.62,275.62h498.75c151.88,0,275.62-123.75,275.62-275.62v-498.75C1049.99,123.74,926.24,0,774.37,0ZM794.99,300l-142.5,333.75c-1.88,5.62-5.62,9.38-9.38,13.12-1.88,1.88-5.62,3.75-7.5,3.75l-335.62,144.38c-13.12,5.62-28.12,1.88-37.5-7.5s-13.12-24.38-7.5-37.5l142.5-333.75c3.75-7.5,9.38-13.12,16.88-16.88l335.62-144.38c13.12-5.62,28.12-1.88,37.5,7.5s13.12,24.38,7.5,37.5Z"/>
                      <g>
                        <path fill="#fff" d="M940.09,883.92c-58.79,19.42-78.37,39-97.79,97.79-1.6,4.83-8.43,4.83-10.03,0-19.42-58.79-39-78.37-97.79-97.79-4.83-1.6-4.83-8.43,0-10.03,58.79-19.42,78.37-39,97.79-97.79,1.6-4.83,8.43-4.83,10.03,0,19.42,58.79,39,78.37,97.79,97.79,4.83,1.6,4.83,8.43,0,10.03h0Z"/>
                        <path fill="#fff" d="M955.34,763.84c-27.23,9-36.3,18.07-45.3,45.3-.74,2.24-3.91,2.24-4.65,0-9-27.23-18.07-36.3-45.3-45.3-2.24-.74-2.24-3.91,0-4.65,27.23-9,36.3-18.07,45.3-45.3.74-2.24,3.91-2.24,4.65,0,9,27.23,18.07,36.3,45.3,45.3,2.24.74,2.24,3.91,0,4.65h0Z"/>
                        <g>
                          <path fill="#fff" d="M109.89,166.08c58.79-19.42,78.37-39,97.79-97.79,1.6-4.83,8.43-4.83,10.03,0,19.42,58.79,39,78.37,97.79,97.79,4.83,1.6,4.83,8.43,0,10.03-58.79,19.42-78.37,39-97.79,97.79-1.6,4.83-8.43,4.83-10.03,0-19.42-58.79-39-78.37-97.79-97.79-4.83-1.6-4.83-8.43,0-10.03h0Z"/>
                          <path fill="#fff" d="M94.64,286.16c27.23-9,36.3-18.07,45.3-45.3.74-2.24,3.91-2.24,4.65,0,9,27.23,18.07,36.3,45.3,45.3,2.24.74,2.24,3.91,0,4.65-27.23,9-36.3,18.07-45.3,45.3-.74,2.24-3.91,2.24-4.65,0-9-27.23-18.07-36.3-45.3-45.3-2.24-.74-2.24-3.91,0-4.65h0Z"/>
                        </g>
                      </g>
                    </svg>
                  )}
                </div>

                {/* Message */}
                <div className={`flex flex-1 flex-col gap-1 ${m.role === "user" ? "items-end" : "items-start"}`}>
                  <p className={`text-[13px] font-normal leading-normal text-neutral-500 ${m.role === "user" ? "mr-1" : "ml-1"}`}>
                    {m.role === "user" ? "User" : "Assistant"}
                  </p>
                  <div
                    className={`flex max-w-[85%] flex-col gap-2 rounded-xl px-5 py-3 text-base font-normal leading-normal md:max-w-[70%] ${
                      m.role === "user"
                        ? "rounded-tr-none text-white shadow-md"
                        : "rounded-tl-none bg-[#e8f4ff] text-neutral-900"
                    }`}
                    style={m.role === "user" ? { backgroundColor: '#0052ff' } : undefined}
                  >
                    {m.role === "assistant" ? (
                      <FormattedMessage content={m.content} />
                    ) : (
                      <p>{m.content}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {isSending && (
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#FFFFFF] text-white border border-gray-200">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 1049.99 1049.99">
                    <path fill="#0052fe" d="M350.61,699.38l210-90-120-120-90,210Z"/>
                    <path fill="#0052fe" d="M774.37,0h-498.75C123.74,0,0,123.75,0,275.62v498.75c0,151.88,123.75,275.62,275.62,275.62h498.75c151.88,0,275.62-123.75,275.62-275.62v-498.75C1049.99,123.74,926.24,0,774.37,0ZM794.99,300l-142.5,333.75c-1.88,5.62-5.62,9.38-9.38,13.12-1.88,1.88-5.62,3.75-7.5,3.75l-335.62,144.38c-13.12,5.62-28.12,1.88-37.5-7.5s-13.12-24.38-7.5-37.5l142.5-333.75c3.75-7.5,9.38-13.12,16.88-16.88l335.62-144.38c13.12-5.62,28.12-1.88,37.5,7.5s13.12,24.38,7.5,37.5Z"/>
                    <g>
                      <path fill="#fff" d="M940.09,883.92c-58.79,19.42-78.37,39-97.79,97.79-1.6,4.83-8.43,4.83-10.03,0-19.42-58.79-39-78.37-97.79-97.79-4.83-1.6-4.83-8.43,0-10.03,58.79-19.42,78.37-39,97.79-97.79,1.6-4.83,8.43-4.83,10.03,0,19.42,58.79,39,78.37,97.79,97.79,4.83,1.6,4.83,8.43,0,10.03h0Z"/>
                      <path fill="#fff" d="M955.34,763.84c-27.23,9-36.3,18.07-45.3,45.3-.74,2.24-3.91,2.24-4.65,0-9-27.23-18.07-36.3-45.3-45.3-2.24-.74-2.24-3.91,0-4.65,27.23-9,36.3-18.07,45.3-45.3.74-2.24,3.91-2.24,4.65,0,9,27.23,18.07,36.3,45.3,45.3,2.24.74,2.24,3.91,0,4.65h0Z"/>
                      <g>
                        <path fill="#fff" d="M109.89,166.08c58.79-19.42,78.37-39,97.79-97.79,1.6-4.83,8.43-4.83,10.03,0,19.42,58.79,39,78.37,97.79,97.79,4.83,1.6,4.83,8.43,0,10.03-58.79,19.42-78.37,39-97.79,97.79-1.6,4.83-8.43,4.83-10.03,0-19.42-58.79-39-78.37-97.79-97.79-4.83-1.6-4.83-8.43,0-10.03h0Z"/>
                        <path fill="#fff" d="M94.64,286.16c27.23-9,36.3-18.07,45.3-45.3.74-2.24,3.91-2.24,4.65,0,9,27.23,18.07,36.3,45.3,45.3,2.24.74,2.24,3.91,0,4.65-27.23,9-36.3,18.07-45.3,45.3-.74,2.24-3.91,2.24-4.65,0-9-27.23-18.07-36.3-45.3-45.3-2.24-.74-2.24-3.91,0-4.65h0Z"/>
                      </g>
                    </g>
                  </svg>
              </div>
                <div className="flex flex-1 flex-col gap-1 items-start">
                  <p className="ml-1 text-[13px] font-normal leading-normal text-neutral-500">Assistant</p>
                  <div className="max-w-[85%] rounded-xl rounded-tl-none bg-[#e8f4ff] px-5 py-3 text-base font-normal leading-normal text-neutral-900 md:max-w-[70%]">
                    <div className="flex items-center gap-2">
                      <span>{loadingStatus || 'Processing...'}</span>
                      <div className="flex gap-1">
                        <span className="animate-bounce animation-delay-0">.</span>
                        <span className="animate-bounce animation-delay-150">.</span>
                        <span className="animate-bounce animation-delay-300">.</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>


          {/* Filter Buttons */}
          <div className="border-t border-transparent px-4 pb-3 pt-2 md:px-6">
            <div className="flex gap-2 overflow-x-auto pb-2">
              {suggestions.map((s) => (
                <button
                  key={s}
                  type="button"
                  className="flex h-8 text-gray-600 shrink-0 items-center justify-center gap-x-2 rounded-full border border-gray-200 bg-white px-4 transition-colors hover:bg-gray-50"
                  disabled={isSending || trips.length === 0}
                  onClick={() => handleFilterClick(s)}
                >
                  <span className="whitespace-nowrap text-sm font-medium leading-normal">{s}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Input Area */}
          <div className="border-t border-gray-100 bg-white p-4 md:p-6">
            <div className="flex w-full items-end gap-2 rounded-full border border-gray-200 bg-[#F2F7FC] p-2 transition-colors focus-within:border-blue-300">
              
              <textarea
                className="form-textarea flex max-h-32 min-w-0 flex-1 resize-none overflow-hidden border-none bg-transparent px-3 py-3 text-base font-normal leading-normal text-neutral-900 placeholder:text-neutral-400 placeholder:whitespace-nowrap focus:outline-none focus:ring-0"
                placeholder="Type your message..."
                rows={1}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    void sendMessage(input);
                  }
                }}
              />
              <button
                className="flex h-12 min-w-[48px] shrink-0 items-center justify-center overflow-hidden rounded-full text-blue-600 transition-colors bg-[#E4F2FF]"
                onClick={startRecording}
                disabled={isSending || isRecording}
                title="Start voice input"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6.875 3.75C6.875 2.9212 7.20424 2.12634 7.79029 1.54029C8.37634 0.95424 9.1712 0.625 10 0.625C10.8288 0.625 11.6237 0.95424 12.2097 1.54029C12.7958 2.12634 13.125 2.9212 13.125 3.75V10.625C13.125 11.4538 12.7958 12.2487 12.2097 12.8347C11.6237 13.4208 10.8288 13.75 10 13.75C9.1712 13.75 8.37634 13.4208 7.79029 12.8347C7.20424 12.2487 6.875 11.4538 6.875 10.625V3.75Z" fill="#0052FF"/>
                <path d="M5 8.75C5.16576 8.75 5.32473 8.81585 5.44194 8.93306C5.55915 9.05027 5.625 9.20924 5.625 9.375V10.625C5.625 11.7853 6.08594 12.8981 6.90641 13.7186C7.72688 14.5391 8.83968 15 10 15C11.1603 15 12.2731 14.5391 13.0936 13.7186C13.9141 12.8981 14.375 11.7853 14.375 10.625V9.375C14.375 9.20924 14.4408 9.05027 14.5581 8.93306C14.6753 8.81585 14.8342 8.75 15 8.75C15.1658 8.75 15.3247 8.81585 15.4419 8.93306C15.5592 9.05027 15.625 9.20924 15.625 9.375V10.625C15.625 12.0087 15.115 13.3438 14.1926 14.3752C13.2702 15.4066 12.0001 16.0619 10.625 16.2158V18.125H13.125C13.2908 18.125 13.4497 18.1908 13.5669 18.3081C13.6842 18.4253 13.75 18.5842 13.75 18.75C13.75 18.9158 13.6842 19.0747 13.5669 19.1919C13.4497 19.3092 13.2908 19.375 13.125 19.375H6.875C6.70924 19.375 6.55027 19.3092 6.43306 19.1919C6.31585 19.0747 6.25 18.9158 6.25 18.75C6.25 18.5842 6.31585 18.4253 6.43306 18.3081C6.55027 18.1908 6.70924 18.125 6.875 18.125H9.375V16.2158C7.9999 16.0619 6.72976 15.4066 5.80736 14.3752C4.88497 13.3438 4.37502 12.0087 4.375 10.625V9.375C4.375 9.20924 4.44085 9.05027 4.55806 8.93306C4.67527 8.81585 4.83424 8.75 5 8.75Z" fill="#0052FF"/>
                </svg>
              </button>
              {isSending ? (
                <button
                  className="flex h-12 min-w-[48px] shrink-0 items-center justify-center overflow-hidden rounded-full bg-red-600 text-white transition-colors hover:bg-red-700"
                  onClick={stopChatResponse}
                  title="Stop generating response"
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <rect x="6" y="6" width="12" height="12" rx="1" />
                  </svg>
                </button>
              ) : (
                <button
                  className="flex h-12 min-w-[48px] shrink-0 items-center justify-center overflow-hidden rounded-full text-white transition-colors disabled:opacity-50"
                  style={{ backgroundColor: input.trim() ? '#a8c5ff' : '#e5e7eb' }}
                  disabled={!input.trim()}
                  onClick={() => void sendMessage(input)}
                  onMouseEnter={(e) => input.trim() && (e.currentTarget.style.backgroundColor = '#8fb3ff')}
                  onMouseLeave={(e) => input.trim() && (e.currentTarget.style.backgroundColor = '#a8c5ff')}
                >
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clipPath="url(#clip0_6630_18894)">
                  <path d="M16 27V5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M7 14L16 5L25 14" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </g>
                  <defs>
                  <clipPath id="clip0_6630_18894">
                  <rect width="32" height="32" fill="white"/>
                  </clipPath>
                  </defs>
                  </svg>
                </button>
              )}
            </div>

            {/* Action Buttons */}
            <div className="mt-3 flex justify-center gap-4">
              {/* Clear Chat Button */}
              {(messages.length > 1 || trips.length > 0) && (
                <button
                  onClick={() => {
                    if (confirm('Clear all chat history and search results?')) {
                      setMessages([
                        {
                          role: "assistant",
                          content:
                            "Kalimera! 🇬🇷 Where would you like to go today?",
                        },
                      ]);
                      setTrips([]);
                      setReturnTrips([]);
                      setSearchParams(null);
                      setExpandedTripIndex(null);
                      setSelectedForwardTrip(null);
                      setSelectedReturnTrip(null);
                      setSortBy('recommended');
                      setIsEditingPorts(false);
                      localStorage.removeItem('ferry-chat-messages');
                      localStorage.removeItem('ferry-trips');
                      localStorage.removeItem('ferry-return-trips');
                      localStorage.removeItem('ferry-search-params');
                      showInfoMessage('Chat cleared successfully');
                    }
                  }}
                  className="text-sm font-semibold text-neutral-500 underline decoration-gray-300 underline-offset-4 hover:text-neutral-900 transition-colors"
                >
                  Clear Chat
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Search Form - Always show */}
        {true && (
          <div className="mt-8 flex flex-col gap-4 rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
            {/* Round Trip Toggle */}
            <div className="flex items-center gap-3 pb-4 border-b border-gray-100">
              {/* Segmented Control for One way / Round Trip */}
              <div className="inline-flex rounded-full bg-gray-100 p-1">
                <button
                  onClick={() => {
                    if (isEditingPorts) {
                      setEditIsRoundTrip(false);
                    } else {
                      if (searchParams?.isRoundTrip) {
                        handleToggleRoundTrip();
                      }
                    }
                  }}
                  className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                    !(isEditingPorts ? editIsRoundTrip : searchParams?.isRoundTrip)
                      ? 'bg-[#D6E8F7] text-neutral-900 shadow-sm'
                      : 'text-neutral-600 hover:text-neutral-900'
                  }`}
                >
                  One way
                </button>
                <button
                  onClick={() => {
                    if (isEditingPorts) {
                      if (!editIsRoundTrip) {
                        setEditIsRoundTrip(true);
                        const today = new Date(editDate);
                        today.setDate(today.getDate() + 7);
                        const defaultReturn = today.toISOString().slice(0, 10);
                        setEditReturnDate(defaultReturn);
                      }
                    } else {
                      if (!searchParams?.isRoundTrip) {
                        handleToggleRoundTrip();
                      }
                    }
                  }}
                  className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                    (isEditingPorts ? editIsRoundTrip : searchParams?.isRoundTrip)
                      ? 'bg-[#D6E8F7] text-neutral-900 shadow-sm'
                      : 'text-neutral-600 hover:text-neutral-900'
                  }`}
                >
                  Round Trip
                </button>
              </div>
            </div>

            {/* Horizontal Search Form */}
            <div className="flex flex-col lg:flex-row gap-3 items-stretch lg:items-end">
              {/* From/To Container - Takes most space */}
              <div className="border border-[#E4F2FF] rounded-lg lg:rounded-full px-4 py-2 flex flex-col lg:flex-row gap-3 w-full lg:flex-[2]">
                {/* From Field */}
                <div className="relative flex-1" data-dropdown="departure">
                  <label htmlFor="departure-input" className="text-xs font-medium text-[#737373] block px-1">From</label>
                  <input
                    id="departure-input"
                    type="text"
                    value={editDeparture}
                    onChange={(e) => {
                      setEditDeparture(e.target.value);
                      setShowDepartureDropdown(true);
                    }}
                    onFocus={() => setShowDepartureDropdown(true)}
                    className="w-full rounded-lg border-0 px-1 py-1 text-sm text-[#737373] bg-white placeholder:whitespace-nowrap focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    placeholder="Island, city or port"
                    autoComplete="off"
                  />
                  {showDepartureDropdown && filteredDeparturePorts.length > 0 && (
                    <div className="absolute left-0 top-full z-30 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg max-h-60 overflow-y-auto">
                      {filteredDeparturePorts.map((port) => (
                        <button
                          key={port.code}
                          onClick={() => handleSelectDeparturePort(port)}
                          className="w-full px-4 py-2 text-left text-sm text-neutral-900 hover:bg-gray-50 flex justify-between"
                        >
                          <span className="font-medium">{port.name}</span>
                          <span className="text-xs text-gray-500">{port.code}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Swap Button */}
                <div className="flex items-center justify-center lg:mb-3">
                  <button
                    onClick={() => {
                      // Swap display values
                      const temp = editDeparture;
                      setEditDeparture(editArrival);
                      setEditArrival(temp);
                      
                      // Also swap in searchParams if they exist
                      if (searchParams) {
                        const updatedParams = {
                          ...searchParams,
                          departurePort: searchParams.arrivalPort,
                          arrivalPort: searchParams.departurePort,
                          departurePortCode: searchParams.arrivalPortCode,
                          arrivalPortCode: searchParams.departurePortCode,
                          isRoundTrip: searchParams.isRoundTrip,
                          returnDate: searchParams.isRoundTrip ? searchParams.returnDate : undefined,
                        };
                        setSearchParams(updatedParams);
                      }
                    }}
                    className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                    title="Swap ports"
                  >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g clipPath="url(#clip0_6630_18909)">
                    <path d="M13.75 11.25L16.25 13.75L13.75 16.25" stroke="#0052FF" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M3.75 13.75H16.25" stroke="#0052FF" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M6.25 8.75L3.75 6.25L6.25 3.75" stroke="#0052FF" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M16.25 6.25H3.75" stroke="#0052FF" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
                    </g>
                    <defs>
                    <clipPath id="clip0_6630_18909">
                    <rect width="20" height="20" fill="white"/>
                    </clipPath>
                    </defs>
                    </svg>

                  </button>
                </div>

                {/* To Field */}
                <div className="relative flex-1" data-dropdown="arrival">
                  <label htmlFor="arrival-input" className="text-xs font-medium text-[#737373] block px-1">To</label>
                  <input
                    id="arrival-input"
                    type="text"
                    value={editArrival}
                    onChange={(e) => {
                      setEditArrival(e.target.value);
                      setShowArrivalDropdown(true);
                    }}
                    onFocus={() => setShowArrivalDropdown(true)}
                    className="w-full rounded-lg border-0 px-1 py-1 text-sm text-[#737373] bg-white focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    placeholder="Island, city or port"
                    autoComplete="off"
                  />
                  {showArrivalDropdown && filteredArrivalPorts.length > 0 && (
                    <div className="absolute left-0 top-full z-30 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg max-h-60 overflow-y-auto">
                      {filteredArrivalPorts.map((port) => (
                        <button
                          key={port.code}
                          onClick={() => handleSelectArrivalPort(port)}
                          className="w-full px-4 py-2 text-left text-sm text-neutral-900 hover:bg-gray-50 flex justify-between"
                        >
                          <span className="font-medium">{port.name}</span>
                          <span className="text-xs text-gray-500">{port.code}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Date Picker - Medium width */}
              <div className="flex flex-col lg:flex-row gap-3 w-full lg:w-auto">
                {/* Departure Date Picker */}
                <div className="border border-[#E4F2FF] rounded-full px-4 py-3 relative w-full lg:w-52">
                  <label htmlFor="departure-date-button" className="text-xs font-medium text-[#737373] block px-4">
                    Departure
                  </label>
                  <button
                    id="departure-date-button"
                    onClick={() => {
                      const dateToShow = isEditingPorts 
                        ? (editDate || new Date().toISOString().split('T')[0])
                        : searchParams?.date;
                      if (!showCalendar && dateToShow) {
                        const selectedDate = new Date(dateToShow + 'T00:00:00');
                        setCalendarMonth(selectedDate);
                      }
                      setShowCalendar(!showCalendar);
                    }}
                    className="w-full flex items-center gap-3 rounded-full px-4 text-sm text-neutral-900 bg-white hover:border-blue-500 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 transition-colors"
                  >
                    <div className="flex-1 text-left">
                      <div className="font-medium">
                        {formatDateDisplay(isEditingPorts ? editDate : searchParams?.date || editDate)}
                      </div>
                    </div>
                  </button>

                  {/* Departure Date Calendar */}
                  {showCalendar && (
                    <div className="absolute left-0 top-full z-30 mt-2 rounded-xl border border-gray-100 bg-white p-4 shadow-xl w-80">
                      <div className="mb-4 flex items-center justify-between">
                        <button
                          onClick={handlePreviousMonth}
                          className="rounded-full p-1 transition-colors hover:bg-gray-100 cursor-pointer"
                        >
                          <span className="text-gray-500">←</span>
                        </button>
                        <span className="text-sm font-bold text-neutral-900">
                          {calendarMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                        </span>
                        <button
                          onClick={handleNextMonth}
                          className="rounded-full p-1 transition-colors hover:bg-gray-100 cursor-pointer"
                        >
                          <span className="text-gray-500">→</span>
                        </button>
                      </div>
                      <div className="mb-2 grid grid-cols-7 gap-1 text-center">
                        {['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'].map(day => (
                          <span key={day} className="text-[10px] font-bold text-gray-400">{day}</span>
                        ))}
                      </div>
                      <div className="grid grid-cols-7 gap-1 text-center text-sm">
                        {generateCalendarDays(calendarMonth).map((dayObj, idx) => (
                          <button
                            key={idx}
                            onClick={() => {
                              if (dayObj.isCurrentMonth) {
                                if (isEditingPorts) {
                                  setEditDate(dayObj.date);
                                  // If round trip and return date is before new departure, adjust it
                                  if (editIsRoundTrip && editReturnDate && dayObj.date >= editReturnDate) {
                                    const newReturn = new Date(dayObj.date);
                                    newReturn.setDate(newReturn.getDate() + 1);
                                    setEditReturnDate(newReturn.toISOString().split('T')[0]);
                                  }
                                } else {
                                  handleSelectDate(dayObj.date);
                                }
                                setShowCalendar(false);
                              }
                            }}
                            disabled={!dayObj.isCurrentMonth}
                            className={`p-2 rounded-full transition-colors ${
                              dayObj.date === (isEditingPorts ? editDate : searchParams?.date)
                                ? 'bg-blue-600 text-white font-bold shadow-md'
                                : dayObj.isCurrentMonth
                                ? 'hover:bg-gray-100 text-neutral-900 cursor-pointer'
                                : 'text-gray-300 cursor-default'
                            }`}
                          >
                            {dayObj.day}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Return Date Picker - Only show for round trips */}
                {(isEditingPorts ? editIsRoundTrip : searchParams?.isRoundTrip) && (
                  <div className="border border-[#E4F2FF] rounded-full px-4 py-2 relative w-full lg:w-52">
                    <label htmlFor="return-date-button" className="text-xs font-medium text-[#737373] block px-4">
                      Return
                    </label>
                    <button
                      id="return-date-button"
                      onClick={() => {
                        const dateToShow = isEditingPorts 
                          ? (editReturnDate || editDate || new Date().toISOString().split('T')[0])
                          : searchParams?.returnDate;
                        if (!showReturnCalendar && dateToShow) {
                          const selectedDate = new Date(dateToShow + 'T00:00:00');
                          setReturnCalendarMonth(selectedDate);
                        }
                        setShowReturnCalendar(!showReturnCalendar);
                      }}
                      
                      className="w-full flex items-center gap-3 rounded-full px-4 text-sm text-neutral-900 bg-white hover:border-blue-500 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 transition-colors"
                    >
                      <div className="flex-1 text-left">
                        <div className="font-medium">
                          {formatDateDisplay(isEditingPorts ? (editReturnDate || editDate) : (searchParams?.returnDate || searchParams?.date || editDate))}
                        </div>
                      </div>
                    </button>

                    {/* Return Date Calendar */}
                    {showReturnCalendar && (
                      <div className="absolute left-0 top-full z-30 mt-2 rounded-xl border border-gray-100 bg-white p-4 shadow-xl w-80">
                        <div className="mb-4 flex items-center justify-between">
                          <button
                            onClick={handlePreviousReturnMonth}
                            className="rounded-full p-1 transition-colors hover:bg-gray-100 cursor-pointer"
                          >
                            <span className="text-gray-500">←</span>
                          </button>
                          <span className="text-sm font-bold text-neutral-900">
                            {returnCalendarMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                          </span>
                          <button
                            onClick={handleNextReturnMonth}
                            className="rounded-full p-1 transition-colors hover:bg-gray-100 cursor-pointer"
                          >
                            <span className="text-gray-500">→</span>
                          </button>
                        </div>
                        <div className="mb-2 grid grid-cols-7 gap-1 text-center">
                          {['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'].map(day => (
                            <span key={day} className="text-[10px] font-bold text-gray-400">{day}</span>
                          ))}
                        </div>
                        <div className="grid grid-cols-7 gap-1 text-center text-sm">
                          {generateCalendarDays(returnCalendarMonth).map((dayObj, idx) => {
                            const departureDate = isEditingPorts ? editDate : searchParams?.date;
                            const currentReturnDate = isEditingPorts ? editReturnDate : searchParams?.returnDate;
                            return (
                              <button
                                key={idx}
                                onClick={() => {
                                  if (dayObj.isCurrentMonth && departureDate && dayObj.date > departureDate) {
                                    if (isEditingPorts) {
                                      setEditReturnDate(dayObj.date);
                                    } else {
                                      handleSelectReturnDate(dayObj.date);
                                    }
                                    setShowReturnCalendar(false);
                                  }
                                }}
                                disabled={!dayObj.isCurrentMonth || !departureDate || dayObj.date <= departureDate}
                                className={`p-2 rounded-full transition-colors ${
                                  dayObj.date === currentReturnDate
                                    ? 'bg-green-600 text-white font-bold shadow-md'
                                    : dayObj.isCurrentMonth && departureDate && dayObj.date > departureDate
                                    ? 'hover:bg-gray-100 text-neutral-900 cursor-pointer'
                                    : 'text-gray-300 cursor-default'
                                }`}
                              >
                                {dayObj.day}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Search Button - Fixed width */}
              <button
                onClick={handleApplyPortChanges}
                disabled={isLoadingTrips}
                className="w-full lg:w-auto lg:whitespace-nowrap flex items-center justify-center gap-2 rounded-full px-8 py-3 text-base text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: '#0052ff' }}
                onMouseEnter={(e) => !isLoadingTrips && (e.currentTarget.style.backgroundColor = '#0041cc')}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#0052ff'}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" clipPath="evenodd" d="M12.5627 3.98418C13.5677 6.3484 13.0739 8.97411 11.5088 10.8019L15.8529 15.1466C16.0482 15.3418 16.0481 15.6584 15.8529 15.8536C15.6576 16.0488 15.3411 16.0488 15.1459 15.8535L10.8021 11.5091C10.3053 11.935 9.73558 12.293 9.1013 12.5627C5.77714 13.9758 1.93604 12.4257 0.522822 9.1013C-0.890274 5.77713 0.659738 1.93603 3.98418 0.522818L4.15966 0.451162C7.44294 -0.835292 11.1742 0.717957 12.5627 3.98418ZM4.5352 1.37791L4.37532 1.44299L4.21752 1.51293C1.51038 2.76283 0.268525 5.94734 1.44298 8.71014C2.64018 11.5264 5.89421 12.8395 8.71014 11.6425C9.37538 11.3597 9.95675 10.9622 10.441 10.4827L10.4606 10.4606C10.4674 10.4538 10.4743 10.4473 10.4813 10.441C12.0162 8.89034 12.5488 6.50728 11.6425 4.37534C10.4678 1.61206 7.31385 0.296054 4.5352 1.37791Z" fill="white"/>
                </svg>
                <span>Search</span>
              </button>
            </div>
          </div>
        )}

        {(() => {
          const tripsAndReturnContent = (
            <>
        {/* Trips Section */}
        <div ref={tripsRef} className="flex flex-col gap-5">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between px-2">
            {searchParams?.isRoundTrip && searchParams.returnDate ? (
              searchParams && (
                <h3 className="text-xl font-bold text-neutral-900">
                  {searchParams.departurePort} → {searchParams.arrivalPort}
                </h3>
              )
            ) : (
            <div className="flex flex-col gap-2">
              <h2 className="text-xl font-bold text-neutral-600 flex items-center gap-2">
                <span>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15.957 18.0677C16.4388 18.5235 16.9922 18.8588 17.6172 19.0736C18.2422 19.2885 18.8867 19.3503 19.5508 19.2592C19.6289 19.2592 19.7005 19.2819 19.7656 19.3275C19.8307 19.3731 19.8698 19.4414 19.8828 19.5326C19.8958 19.6107 19.8763 19.6823 19.8242 19.7474C19.7721 19.8125 19.7005 19.8516 19.6094 19.8646C18.9453 19.9427 18.2878 19.8874 17.6367 19.6986C16.9857 19.5098 16.3932 19.2071 15.8594 18.7904L15.7617 18.6927L15.6641 18.7904C15.2604 19.155 14.8177 19.4284 14.3359 19.6107C13.8411 19.78 13.3398 19.8646 12.832 19.8646C12.3242 19.8646 11.8294 19.767 11.3477 19.5717C10.8659 19.3894 10.4297 19.1159 10.0391 18.7513L10 18.7123L9.92188 18.7904C9.51823 19.155 9.06901 19.4284 8.57422 19.6107C8.07943 19.78 7.58138 19.8646 7.08008 19.8646C6.57878 19.8646 6.08724 19.767 5.60547 19.5717C5.11068 19.3894 4.66797 19.1159 4.27734 18.7513L4.23828 18.6927L4.14062 18.7904C3.63281 19.1941 3.06641 19.4903 2.44141 19.6791C1.81641 19.8679 1.17839 19.9362 0.527344 19.8842L0.371094 19.8646C0.292969 19.8516 0.227865 19.8125 0.175781 19.7474C0.123698 19.6823 0.104167 19.6107 0.117188 19.5326C0.130208 19.4414 0.169271 19.3731 0.234375 19.3275C0.299479 19.2819 0.371094 19.2592 0.449219 19.2592C1.11328 19.3503 1.75781 19.2885 2.38281 19.0736C3.00781 18.8588 3.5612 18.5235 4.04297 18.0677C4.10807 18.0157 4.18294 17.9896 4.26758 17.9896C4.35221 17.9896 4.42057 18.0222 4.47266 18.0873C5.13672 18.8164 5.96029 19.2071 6.94336 19.2592C7.92643 19.3112 8.78255 19.0052 9.51172 18.3412C9.5638 18.3021 9.60938 18.2598 9.64844 18.2142C9.6875 18.1687 9.73307 18.1263 9.78516 18.0873C9.83724 18.0222 9.90885 17.9896 10 17.9896C10.0911 17.9896 10.1628 18.0222 10.2148 18.0873C10.8789 18.8164 11.7025 19.2071 12.6855 19.2592C13.6686 19.3112 14.5312 19.0052 15.2734 18.3412L15.4004 18.2142L15.5273 18.0873C15.5794 18.0222 15.6478 17.9896 15.7324 17.9896C15.8171 17.9896 15.8919 18.0157 15.957 18.0677ZM10.6445 0.098999C10.9049 0.098999 11.1263 0.190145 11.3086 0.372437C11.4909 0.554728 11.582 0.776082 11.582 1.0365V3.92712H12.5586C13.3268 3.92712 13.9876 4.19731 14.541 4.73767C15.0944 5.27804 15.3841 5.93233 15.4102 6.70056V6.77869V11.0756L19.0625 12.5404C19.1276 12.5795 19.1797 12.6348 19.2188 12.7064C19.2578 12.778 19.2578 12.8529 19.2188 12.931C19.1927 13.0092 19.1471 13.0645 19.082 13.097C19.0169 13.1296 18.9453 13.1329 18.8672 13.1068H18.8281L17.5391 12.5795L16.2305 16.5248C16.2044 16.6029 16.1523 16.6615 16.0742 16.7006C15.9961 16.7396 15.918 16.7461 15.8398 16.7201C15.7747 16.6941 15.7227 16.6485 15.6836 16.5834C15.6445 16.5183 15.6315 16.4467 15.6445 16.3685L15.6641 16.3295L16.1328 14.9037L10.293 12.5599V15.724C10.293 15.8151 10.2637 15.89 10.2051 15.9486C10.1465 16.0072 10.0781 16.0365 10 16.0365C9.92188 16.0365 9.85677 16.0105 9.80469 15.9584C9.7526 15.9063 9.72005 15.8412 9.70703 15.7631V15.724V12.5599L3.86719 14.9037L4.27734 16.1537C4.30339 16.2318 4.29688 16.3067 4.25781 16.3783C4.21875 16.4499 4.16016 16.4987 4.08203 16.5248C4.01693 16.5508 3.94857 16.5476 3.87695 16.515C3.80534 16.4825 3.75651 16.4336 3.73047 16.3685L3.71094 16.3295L2.46094 12.5795L1.17188 13.1068C1.09375 13.1329 1.01562 13.1296 0.9375 13.097C0.859375 13.0645 0.807292 13.0092 0.78125 12.931C0.755208 12.8659 0.755208 12.7976 0.78125 12.726C0.807292 12.6543 0.846354 12.599 0.898438 12.5599L0.9375 12.5404L4.58984 11.0756V6.77869C4.58984 6.01046 4.85677 5.34965 5.39062 4.79626C5.92448 4.24288 6.57552 3.95317 7.34375 3.92712H7.44141H8.41797V1.0365C8.41797 0.789103 8.5026 0.574259 8.67188 0.391968C8.84115 0.209676 9.04948 0.11202 9.29688 0.098999H9.35547H10.6445ZM9.70703 9.68884L9.62891 9.70837L5 11.5638L3.00781 12.3646L3.67188 14.3373L9.70703 11.9154V9.68884ZM10.293 9.68884V11.9154L16.3281 14.3373L16.9922 12.3646L15 11.5638H14.9805L10.293 9.68884ZM12.5586 4.53259H7.44141C6.82943 4.53259 6.30859 4.74418 5.87891 5.16736C5.44922 5.59054 5.22135 6.1016 5.19531 6.70056V6.77869V10.8412L9.41406 9.1615C9.50521 9.12244 9.59961 9.09314 9.69727 9.07361C9.79492 9.05408 9.89583 9.04431 10 9.04431C10.0781 9.04431 10.1595 9.05082 10.2441 9.06384C10.3288 9.07686 10.4102 9.09639 10.4883 9.12244L10.5859 9.1615L14.8047 10.8412V6.77869C14.8047 6.17973 14.5964 5.66215 14.1797 5.22595C13.763 4.78975 13.2487 4.55863 12.6367 4.53259H12.5586ZM12.5586 6.48572C12.6367 6.48572 12.7051 6.51501 12.7637 6.57361C12.8223 6.6322 12.8516 6.70056 12.8516 6.77869C12.8516 6.85681 12.8288 6.92517 12.7832 6.98376C12.7376 7.04236 12.6758 7.07817 12.5977 7.09119H12.5586H7.44141C7.36328 7.09119 7.29492 7.06189 7.23633 7.0033C7.17773 6.9447 7.14844 6.86983 7.14844 6.77869C7.14844 6.71358 7.17122 6.65173 7.2168 6.59314C7.26237 6.53455 7.32422 6.49874 7.40234 6.48572H7.44141H12.5586ZM10.6445 0.704468H9.35547C9.26432 0.704468 9.1862 0.73702 9.12109 0.802124C9.05599 0.867228 9.02344 0.945353 9.02344 1.0365V3.92712H10.9766V1.0365C10.9766 0.945353 10.9473 0.870483 10.8887 0.81189C10.8301 0.753296 10.7617 0.717489 10.6836 0.704468H10.6445Z" fill="#0052FF"/>
                  </svg>
                </span>
                Available Trips ({trips.length})
                {searchParams && (
                  <>
                    <span className="hidden md:inline text-sm font-normal text-neutral-600">
                      {searchParams.departurePort} → {searchParams.arrivalPort}
                    </span>
                    <span className="hidden md:inline text-sm font-normal text-blue-600">
                      {formatDateDisplay(searchParams.date)}
                    </span>
                  </>
                )}
              </h2>
              {searchParams && (
                <div className="flex md:hidden items-center gap-2 text-sm">
                  <span className="font-normal text-neutral-600">
                    {searchParams.departurePort} → {searchParams.arrivalPort}
                  </span>
                  <span className="font-normal text-blue-600">
                    {formatDateDisplay(searchParams.date)}
                  </span>
                </div>
              )}
            </div>
            )}

            {/* Sort Dropdown */}
            <div className="flex items-center gap-2">
                <span className="hidden text-sm text-neutral-600 lg:block">Sort by:</span>
                <select
                  className="cursor-pointer border-none bg-transparent py-0 pr-8 text-sm font-semibold text-neutral-900 focus:ring-0"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as "recommended" | "price" | "duration")}
                >
                  <option value="recommended">Recommended</option>
                  <option value="price">Price (Low to High)</option>
                  <option value="duration">Duration (Shortest)</option>
                </select>
              </div>
          </div>

          <>
          <div className="space-y-5">
            {isLoadingTrips ? (
              <>
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="animate-pulse overflow-hidden rounded-xl border border-gray-100 bg-white p-5 shadow-sm md:p-6"
                  >
                    <div className="grid grid-cols-1 items-center gap-6 md:grid-cols-[1.5fr_2fr_1fr]">
                      <div className="flex items-center gap-4">
                        <div className="h-12 w-12 rounded-lg bg-gray-200"></div>
                        <div className="flex flex-col gap-2 w-32">
                          <div className="h-4 w-full rounded bg-gray-200"></div>
                          <div className="h-3 w-2/3 rounded bg-gray-200"></div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between gap-4">
                        <div className="h-6 w-12 rounded bg-gray-200"></div>
                        <div className="flex-1 flex justify-center">
                          <div className="h-1 w-24 rounded bg-gray-200"></div>
                        </div>
                        <div className="h-6 w-12 rounded bg-gray-200"></div>
                      </div>
                      <div className="flex flex-col items-end gap-3">
                        <div className="h-6 w-20 rounded bg-gray-200"></div>
                        <div className="h-10 w-full rounded-lg bg-gray-200 md:w-24"></div>
                      </div>
                    </div>
                  </div>
                ))}
              </>
            ) : trips.length === 0 ? (
              <div className="rounded-xl border border-dashed border-neutral-200 bg-neutral-50 p-8 text-center flex flex-col items-center ">
                  <svg width="40" height="40" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15.957 18.0677C16.4388 18.5235 16.9922 18.8588 17.6172 19.0736C18.2422 19.2885 18.8867 19.3503 19.5508 19.2592C19.6289 19.2592 19.7005 19.2819 19.7656 19.3275C19.8307 19.3731 19.8698 19.4414 19.8828 19.5326C19.8958 19.6107 19.8763 19.6823 19.8242 19.7474C19.7721 19.8125 19.7005 19.8516 19.6094 19.8646C18.9453 19.9427 18.2878 19.8874 17.6367 19.6986C16.9857 19.5098 16.3932 19.2071 15.8594 18.7904L15.7617 18.6927L15.6641 18.7904C15.2604 19.155 14.8177 19.4284 14.3359 19.6107C13.8411 19.78 13.3398 19.8646 12.832 19.8646C12.3242 19.8646 11.8294 19.767 11.3477 19.5717C10.8659 19.3894 10.4297 19.1159 10.0391 18.7513L10 18.7123L9.92188 18.7904C9.51823 19.155 9.06901 19.4284 8.57422 19.6107C8.07943 19.78 7.58138 19.8646 7.08008 19.8646C6.57878 19.8646 6.08724 19.767 5.60547 19.5717C5.11068 19.3894 4.66797 19.1159 4.27734 18.7513L4.23828 18.6927L4.14062 18.7904C3.63281 19.1941 3.06641 19.4903 2.44141 19.6791C1.81641 19.8679 1.17839 19.9362 0.527344 19.8842L0.371094 19.8646C0.292969 19.8516 0.227865 19.8125 0.175781 19.7474C0.123698 19.6823 0.104167 19.6107 0.117188 19.5326C0.130208 19.4414 0.169271 19.3731 0.234375 19.3275C0.299479 19.2819 0.371094 19.2592 0.449219 19.2592C1.11328 19.3503 1.75781 19.2885 2.38281 19.0736C3.00781 18.8588 3.5612 18.5235 4.04297 18.0677C4.10807 18.0157 4.18294 17.9896 4.26758 17.9896C4.35221 17.9896 4.42057 18.0222 4.47266 18.0873C5.13672 18.8164 5.96029 19.2071 6.94336 19.2592C7.92643 19.3112 8.78255 19.0052 9.51172 18.3412C9.5638 18.3021 9.60938 18.2598 9.64844 18.2142C9.6875 18.1687 9.73307 18.1263 9.78516 18.0873C9.83724 18.0222 9.90885 17.9896 10 17.9896C10.0911 17.9896 10.1628 18.0222 10.2148 18.0873C10.8789 18.8164 11.7025 19.2071 12.6855 19.2592C13.6686 19.3112 14.5312 19.0052 15.2734 18.3412L15.4004 18.2142L15.5273 18.0873C15.5794 18.0222 15.6478 17.9896 15.7324 17.9896C15.8171 17.9896 15.8919 18.0157 15.957 18.0677ZM10.6445 0.098999C10.9049 0.098999 11.1263 0.190145 11.3086 0.372437C11.4909 0.554728 11.582 0.776082 11.582 1.0365V3.92712H12.5586C13.3268 3.92712 13.9876 4.19731 14.541 4.73767C15.0944 5.27804 15.3841 5.93233 15.4102 6.70056V6.77869V11.0756L19.0625 12.5404C19.1276 12.5795 19.1797 12.6348 19.2188 12.7064C19.2578 12.778 19.2578 12.8529 19.2188 12.931C19.1927 13.0092 19.1471 13.0645 19.082 13.097C19.0169 13.1296 18.9453 13.1329 18.8672 13.1068H18.8281L17.5391 12.5795L16.2305 16.5248C16.2044 16.6029 16.1523 16.6615 16.0742 16.7006C15.9961 16.7396 15.918 16.7461 15.8398 16.7201C15.7747 16.6941 15.7227 16.6485 15.6836 16.5834C15.6445 16.5183 15.6315 16.4467 15.6445 16.3685L15.6641 16.3295L16.1328 14.9037L10.293 12.5599V15.724C10.293 15.8151 10.2637 15.89 10.2051 15.9486C10.1465 16.0072 10.0781 16.0365 10 16.0365C9.92188 16.0365 9.85677 16.0105 9.80469 15.9584C9.7526 15.9063 9.72005 15.8412 9.70703 15.7631V15.724V12.5599L3.86719 14.9037L4.27734 16.1537C4.30339 16.2318 4.29688 16.3067 4.25781 16.3783C4.21875 16.4499 4.16016 16.4987 4.08203 16.5248C4.01693 16.5508 3.94857 16.5476 3.87695 16.515C3.80534 16.4825 3.75651 16.4336 3.73047 16.3685L3.71094 16.3295L2.46094 12.5795L1.17188 13.1068C1.09375 13.1329 1.01562 13.1296 0.9375 13.097C0.859375 13.0645 0.807292 13.0092 0.78125 12.931C0.755208 12.8659 0.755208 12.7976 0.78125 12.726C0.807292 12.6543 0.846354 12.599 0.898438 12.5599L0.9375 12.5404L4.58984 11.0756V6.77869C4.58984 6.01046 4.85677 5.34965 5.39062 4.79626C5.92448 4.24288 6.57552 3.95317 7.34375 3.92712H7.44141H8.41797V1.0365C8.41797 0.789103 8.5026 0.574259 8.67188 0.391968C8.84115 0.209676 9.04948 0.11202 9.29688 0.098999H9.35547H10.6445ZM9.70703 9.68884L9.62891 9.70837L5 11.5638L3.00781 12.3646L3.67188 14.3373L9.70703 11.9154V9.68884ZM10.293 9.68884V11.9154L16.3281 14.3373L16.9922 12.3646L15 11.5638H14.9805L10.293 9.68884ZM12.5586 4.53259H7.44141C6.82943 4.53259 6.30859 4.74418 5.87891 5.16736C5.44922 5.59054 5.22135 6.1016 5.19531 6.70056V6.77869V10.8412L9.41406 9.1615C9.50521 9.12244 9.59961 9.09314 9.69727 9.07361C9.79492 9.05408 9.89583 9.04431 10 9.04431C10.0781 9.04431 10.1595 9.05082 10.2441 9.06384C10.3288 9.07686 10.4102 9.09639 10.4883 9.12244L10.5859 9.1615L14.8047 10.8412V6.77869C14.8047 6.17973 14.5964 5.66215 14.1797 5.22595C13.763 4.78975 13.2487 4.55863 12.6367 4.53259H12.5586ZM12.5586 6.48572C12.6367 6.48572 12.7051 6.51501 12.7637 6.57361C12.8223 6.6322 12.8516 6.70056 12.8516 6.77869C12.8516 6.85681 12.8288 6.92517 12.7832 6.98376C12.7376 7.04236 12.6758 7.07817 12.5977 7.09119H12.5586H7.44141C7.36328 7.09119 7.29492 7.06189 7.23633 7.0033C7.17773 6.9447 7.14844 6.86983 7.14844 6.77869C7.14844 6.71358 7.17122 6.65173 7.2168 6.59314C7.26237 6.53455 7.32422 6.49874 7.40234 6.48572H7.44141H12.5586ZM10.6445 0.704468H9.35547C9.26432 0.704468 9.1862 0.73702 9.12109 0.802124C9.05599 0.867228 9.02344 0.945353 9.02344 1.0365V3.92712H10.9766V1.0365C10.9766 0.945353 10.9473 0.870483 10.8887 0.81189C10.8301 0.753296 10.7617 0.717489 10.6836 0.704468H10.6445Z" fill="#0052FF"/>
                  </svg>
                {searchParams ? (
                  <>
                    <div className="text-sm font-semibold text-neutral-900">
                      No trips available
                    </div>
                    <div className="mt-1 text-xs text-neutral-500">
                      We couldn't find any ferries from {searchParams.departurePort} to {searchParams.arrivalPort} on {formatDateDisplay(searchParams.date)}. Try a different date or route.
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-sm font-semibold text-neutral-900">
                      No trips to display yet
                    </div>
                    <div className="mt-1 text-xs text-neutral-500">
                      Ask about ferry routes above and I'll search for available options
                    </div>
                    <div className="mt-4 flex flex-wrap justify-center gap-2 text-xs">
                      <span className="rounded-full border border-neutral-200 bg-white px-3 py-1 text-neutral-600">
                        Try: "Athens to Santorini tomorrow"
                      </span>
                      <span className="rounded-full border border-neutral-200 bg-white px-3 py-1 text-neutral-600">
                        Or: "Piraeus to Mykonos June 15"
                      </span>
                    </div>
                  </>
                )}
              </div>
            ) : (
              (() => {
                const allPrices = sortedTrips
                  .map((t) => minAccommodationPriceCents(t))
                  .filter((p): p is number => p !== null);
                const cheapestPrice = allPrices.length > 0 ? Math.min(...allPrices) : null;
                const visibleOutboundTrips = outboundCollapsed
                  ? (selectedForwardTrip ? [selectedForwardTrip] : [])
                  : sortedTrips;

                return visibleOutboundTrips.map((trip, index) => {
                  const segments =
                    (trip as { segments?: Array<Record<string, unknown>> })
                      .segments ?? [];
                  const firstSegment = segments[0] as
                    | {
                        departurePort?: { name?: string };
                        arrivalPort?: { name?: string };
                        departureDateTime?: string;
                        arrivalDateTime?: string;
                        ownerCompany?: { name?: string; iconURL?: string };
                        marketingCompany?: { name?: string; iconURL?: string };
                        vessel?: { name?: string };
                        accommodations?: Array<Record<string, unknown>>;
                      }
                    | undefined;

                  const deepLink = (trip as { deepLink?: string }).deepLink;
                  const minPrice = minAccommodationPriceCents(trip);
                  const isCheapest = minPrice !== null && minPrice === cheapestPrice && trips.length > 1;
                  const hasVehicles = (trip as { hasVehicles?: boolean }).hasVehicles === true;
                  const hasPets = (trip as { hasPets?: boolean }).hasPets === true;
                  const hasETicket = (trip as { hasETicket?: boolean }).hasETicket === true;
                  const accommodations = firstSegment?.accommodations ?? [];
                  const topAccommodations = accommodations.slice(0, 4).map((item) => {
                    const type = (item as { type?: string }).type;
                    const description = type ? formatAccommodationType(type) : "Accommodation";
                    const price =
                      (item as { expectedPrice?: { totalPriceInCents?: number } })
                        .expectedPrice?.totalPriceInCents ?? null;
                    return { description, price };
                  });

                  const isExpanded = expandedTripIndex === index;
                  const duration = calculateDuration(firstSegment?.departureDateTime, firstSegment?.arrivalDateTime);
                  const depTime = formatTime(firstSegment?.departureDateTime);
                  const arrTime = formatTime(firstSegment?.arrivalDateTime);
                  const isSelected = Boolean(searchParams?.isRoundTrip) && selectedForwardTrip === trip;

                  return (
                    <div
                      key={`${index}-${firstSegment?.departureDateTime ?? "trip"}`}
                      className={`group relative overflow-hidden rounded-xl border shadow-sm transition-colors duration-300 hover:shadow-md hover:border-[#0052FF] hover:bg-[#F2F7FC80] ${
                        isSelected
                          ? "border-[#0052FF] bg-[#F2F7FC80]"
                          : isCheapest
                          ? "border-green-500 bg-green-50"
                          : "border-gray-100 bg-white"
                      }`}
                    >
                      {/* Best Price Badge */}
                      {isCheapest && (
                        <div className="absolute left-0 top-0 z-10 rounded-br-lg border-b border-r border-green-200 bg-white/95 px-3 py-1.5 shadow-sm">
                          <div className="text-[11px] font-bold uppercase tracking-wider text-green-700">
                            💰 Best Price
                          </div>
                        </div>
                      )}

                      {/* Main Card Content */}
                      <div className={`grid grid-cols-1 items-center gap-6 p-5 md:grid-cols-[1.5fr_2fr_1fr] md:p-6 ${isCheapest ? 'pt-8 md:pt-6' : ''}`}>
                        {/* Column 1: Company */}
                        <div className="flex items-center gap-4">
                          <div className="flex h-12 w-12 items-center justify-center rounded-lg border border-gray-100 bg-white">
                            {firstSegment?.marketingCompany?.iconURL ? (
                              <img
                                src={firstSegment.marketingCompany.iconURL}
                                alt={firstSegment.marketingCompany?.name ?? firstSegment?.ownerCompany?.name ?? "Company"}
                                className="h-full w-full bg-contain bg-center bg-no-repeat object-contain"
                              />
                            ) : (
                              <div className="text-2xl">
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M15.957 18.0677C16.4388 18.5235 16.9922 18.8588 17.6172 19.0736C18.2422 19.2885 18.8867 19.3503 19.5508 19.2592C19.6289 19.2592 19.7005 19.2819 19.7656 19.3275C19.8307 19.3731 19.8698 19.4414 19.8828 19.5326C19.8958 19.6107 19.8763 19.6823 19.8242 19.7474C19.7721 19.8125 19.7005 19.8516 19.6094 19.8646C18.9453 19.9427 18.2878 19.8874 17.6367 19.6986C16.9857 19.5098 16.3932 19.2071 15.8594 18.7904L15.7617 18.6927L15.6641 18.7904C15.2604 19.155 14.8177 19.4284 14.3359 19.6107C13.8411 19.78 13.3398 19.8646 12.832 19.8646C12.3242 19.8646 11.8294 19.767 11.3477 19.5717C10.8659 19.3894 10.4297 19.1159 10.0391 18.7513L10 18.7123L9.92188 18.7904C9.51823 19.155 9.06901 19.4284 8.57422 19.6107C8.07943 19.78 7.58138 19.8646 7.08008 19.8646C6.57878 19.8646 6.08724 19.767 5.60547 19.5717C5.11068 19.3894 4.66797 19.1159 4.27734 18.7513L4.23828 18.6927L4.14062 18.7904C3.63281 19.1941 3.06641 19.4903 2.44141 19.6791C1.81641 19.8679 1.17839 19.9362 0.527344 19.8842L0.371094 19.8646C0.292969 19.8516 0.227865 19.8125 0.175781 19.7474C0.123698 19.6823 0.104167 19.6107 0.117188 19.5326C0.130208 19.4414 0.169271 19.3731 0.234375 19.3275C0.299479 19.2819 0.371094 19.2592 0.449219 19.2592C1.11328 19.3503 1.75781 19.2885 2.38281 19.0736C3.00781 18.8588 3.5612 18.5235 4.04297 18.0677C4.10807 18.0157 4.18294 17.9896 4.26758 17.9896C4.35221 17.9896 4.42057 18.0222 4.47266 18.0873C5.13672 18.8164 5.96029 19.2071 6.94336 19.2592C7.92643 19.3112 8.78255 19.0052 9.51172 18.3412C9.5638 18.3021 9.60938 18.2598 9.64844 18.2142C9.6875 18.1687 9.73307 18.1263 9.78516 18.0873C9.83724 18.0222 9.90885 17.9896 10 17.9896C10.0911 17.9896 10.1628 18.0222 10.2148 18.0873C10.8789 18.8164 11.7025 19.2071 12.6855 19.2592C13.6686 19.3112 14.5312 19.0052 15.2734 18.3412L15.4004 18.2142L15.5273 18.0873C15.5794 18.0222 15.6478 17.9896 15.7324 17.9896C15.8171 17.9896 15.8919 18.0157 15.957 18.0677ZM10.6445 0.098999C10.9049 0.098999 11.1263 0.190145 11.3086 0.372437C11.4909 0.554728 11.582 0.776082 11.582 1.0365V3.92712H12.5586C13.3268 3.92712 13.9876 4.19731 14.541 4.73767C15.0944 5.27804 15.3841 5.93233 15.4102 6.70056V6.77869V11.0756L19.0625 12.5404C19.1276 12.5795 19.1797 12.6348 19.2188 12.7064C19.2578 12.778 19.2578 12.8529 19.2188 12.931C19.1927 13.0092 19.1471 13.0645 19.082 13.097C19.0169 13.1296 18.9453 13.1329 18.8672 13.1068H18.8281L17.5391 12.5795L16.2305 16.5248C16.2044 16.6029 16.1523 16.6615 16.0742 16.7006C15.9961 16.7396 15.918 16.7461 15.8398 16.7201C15.7747 16.6941 15.7227 16.6485 15.6836 16.5834C15.6445 16.5183 15.6315 16.4467 15.6445 16.3685L15.6641 16.3295L16.1328 14.9037L10.293 12.5599V15.724C10.293 15.8151 10.2637 15.89 10.2051 15.9486C10.1465 16.0072 10.0781 16.0365 10 16.0365C9.92188 16.0365 9.85677 16.0105 9.80469 15.9584C9.7526 15.9063 9.72005 15.8412 9.70703 15.7631V15.724V12.5599L3.86719 14.9037L4.27734 16.1537C4.30339 16.2318 4.29688 16.3067 4.25781 16.3783C4.21875 16.4499 4.16016 16.4987 4.08203 16.5248C4.01693 16.5508 3.94857 16.5476 3.87695 16.515C3.80534 16.4825 3.75651 16.4336 3.73047 16.3685L3.71094 16.3295L2.46094 12.5795L1.17188 13.1068C1.09375 13.1329 1.01562 13.1296 0.9375 13.097C0.859375 13.0645 0.807292 13.0092 0.78125 12.931C0.755208 12.8659 0.755208 12.7976 0.78125 12.726C0.807292 12.6543 0.846354 12.599 0.898438 12.5599L0.9375 12.5404L4.58984 11.0756V6.77869C4.58984 6.01046 4.85677 5.34965 5.39062 4.79626C5.92448 4.24288 6.57552 3.95317 7.34375 3.92712H7.44141H8.41797V1.0365C8.41797 0.789103 8.5026 0.574259 8.67188 0.391968C8.84115 0.209676 9.04948 0.11202 9.29688 0.098999H9.35547H10.6445ZM9.70703 9.68884L9.62891 9.70837L5 11.5638L3.00781 12.3646L3.67188 14.3373L9.70703 11.9154V9.68884ZM10.293 9.68884V11.9154L16.3281 14.3373L16.9922 12.3646L15 11.5638H14.9805L10.293 9.68884ZM12.5586 4.53259H7.44141C6.82943 4.53259 6.30859 4.74418 5.87891 5.16736C5.44922 5.59054 5.22135 6.1016 5.19531 6.70056V6.77869V10.8412L9.41406 9.1615C9.50521 9.12244 9.59961 9.09314 9.69727 9.07361C9.79492 9.05408 9.89583 9.04431 10 9.04431C10.0781 9.04431 10.1595 9.05082 10.2441 9.06384C10.3288 9.07686 10.4102 9.09639 10.4883 9.12244L10.5859 9.1615L14.8047 10.8412V6.77869C14.8047 6.17973 14.5964 5.66215 14.1797 5.22595C13.763 4.78975 13.2487 4.55863 12.6367 4.53259H12.5586ZM12.5586 6.48572C12.6367 6.48572 12.7051 6.51501 12.7637 6.57361C12.8223 6.6322 12.8516 6.70056 12.8516 6.77869C12.8516 6.85681 12.8288 6.92517 12.7832 6.98376C12.7376 7.04236 12.6758 7.07817 12.5977 7.09119H12.5586H7.44141C7.36328 7.09119 7.29492 7.06189 7.23633 7.0033C7.17773 6.9447 7.14844 6.86983 7.14844 6.77869C7.14844 6.71358 7.17122 6.65173 7.2168 6.59314C7.26237 6.53455 7.32422 6.49874 7.40234 6.48572H7.44141H12.5586ZM10.6445 0.704468H9.35547C9.26432 0.704468 9.1862 0.73702 9.12109 0.802124C9.05599 0.867228 9.02344 0.945353 9.02344 1.0365V3.92712H10.9766V1.0365C10.9766 0.945353 10.9473 0.870483 10.8887 0.81189C10.8301 0.753296 10.7617 0.717489 10.6836 0.704468H10.6445Z" fill="#0052FF"/>
                                </svg>
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col">
                            <span className="text-base font-bold text-neutral-900">
                              {firstSegment?.ownerCompany?.name ?? "Ferry Company"}
                            </span>
                            <span className="text-xs text-neutral-600">
                              {firstSegment?.vessel?.name ?? ""}
                            </span>
                          </div>
                        </div>

                        {/* Column 2: Timeline */}
                        <div className="flex items-center justify-between gap-4 px-2">
                          <div className="min-w-[60px] text-center">
                            <span className="block text-xl font-bold text-neutral-900">{depTime}</span>
                            <span className="text-xs text-neutral-600">
                              {firstSegment?.departurePort?.name ?? "Departure"}
                            </span>
                            {firstSegment?.departureDateTime && (
                              <span className="block text-[10px] text-neutral-400 mt-0.5">
                                {new Date(firstSegment.departureDateTime).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </span>
                            )}
                          </div>
                          <div className="flex flex-1 flex-col items-center gap-1">
                            <span className="text-xs text-neutral-600">{duration}</span>
                            <div className="relative h-[2px] w-full bg-gray-200">
                              <div className="absolute -top-[3px] left-0 h-2 w-2 rounded-full bg-gray-300"></div>
                              <div className="absolute -top-[3px] right-0 h-2 w-2 rounded-full bg-gray-300"></div>
                            </div>
                            <span className="text-base text-gray-300">
                              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M15.957 18.0677C16.4388 18.5235 16.9922 18.8588 17.6172 19.0736C18.2422 19.2885 18.8867 19.3503 19.5508 19.2592C19.6289 19.2592 19.7005 19.2819 19.7656 19.3275C19.8307 19.3731 19.8698 19.4414 19.8828 19.5326C19.8958 19.6107 19.8763 19.6823 19.8242 19.7474C19.7721 19.8125 19.7005 19.8516 19.6094 19.8646C18.9453 19.9427 18.2878 19.8874 17.6367 19.6986C16.9857 19.5098 16.3932 19.2071 15.8594 18.7904L15.7617 18.6927L15.6641 18.7904C15.2604 19.155 14.8177 19.4284 14.3359 19.6107C13.8411 19.78 13.3398 19.8646 12.832 19.8646C12.3242 19.8646 11.8294 19.767 11.3477 19.5717C10.8659 19.3894 10.4297 19.1159 10.0391 18.7513L10 18.7123L9.92188 18.7904C9.51823 19.155 9.06901 19.4284 8.57422 19.6107C8.07943 19.78 7.58138 19.8646 7.08008 19.8646C6.57878 19.8646 6.08724 19.767 5.60547 19.5717C5.11068 19.3894 4.66797 19.1159 4.27734 18.7513L4.23828 18.6927L4.14062 18.7904C3.63281 19.1941 3.06641 19.4903 2.44141 19.6791C1.81641 19.8679 1.17839 19.9362 0.527344 19.8842L0.371094 19.8646C0.292969 19.8516 0.227865 19.8125 0.175781 19.7474C0.123698 19.6823 0.104167 19.6107 0.117188 19.5326C0.130208 19.4414 0.169271 19.3731 0.234375 19.3275C0.299479 19.2819 0.371094 19.2592 0.449219 19.2592C1.11328 19.3503 1.75781 19.2885 2.38281 19.0736C3.00781 18.8588 3.5612 18.5235 4.04297 18.0677C4.10807 18.0157 4.18294 17.9896 4.26758 17.9896C4.35221 17.9896 4.42057 18.0222 4.47266 18.0873C5.13672 18.8164 5.96029 19.2071 6.94336 19.2592C7.92643 19.3112 8.78255 19.0052 9.51172 18.3412C9.5638 18.3021 9.60938 18.2598 9.64844 18.2142C9.6875 18.1687 9.73307 18.1263 9.78516 18.0873C9.83724 18.0222 9.90885 17.9896 10 17.9896C10.0911 17.9896 10.1628 18.0222 10.2148 18.0873C10.8789 18.8164 11.7025 19.2071 12.6855 19.2592C13.6686 19.3112 14.5312 19.0052 15.2734 18.3412L15.4004 18.2142L15.5273 18.0873C15.5794 18.0222 15.6478 17.9896 15.7324 17.9896C15.8171 17.9896 15.8919 18.0157 15.957 18.0677ZM10.6445 0.098999C10.9049 0.098999 11.1263 0.190145 11.3086 0.372437C11.4909 0.554728 11.582 0.776082 11.582 1.0365V3.92712H12.5586C13.3268 3.92712 13.9876 4.19731 14.541 4.73767C15.0944 5.27804 15.3841 5.93233 15.4102 6.70056V6.77869V11.0756L19.0625 12.5404C19.1276 12.5795 19.1797 12.6348 19.2188 12.7064C19.2578 12.778 19.2578 12.8529 19.2188 12.931C19.1927 13.0092 19.1471 13.0645 19.082 13.097C19.0169 13.1296 18.9453 13.1329 18.8672 13.1068H18.8281L17.5391 12.5795L16.2305 16.5248C16.2044 16.6029 16.1523 16.6615 16.0742 16.7006C15.9961 16.7396 15.918 16.7461 15.8398 16.7201C15.7747 16.6941 15.7227 16.6485 15.6836 16.5834C15.6445 16.5183 15.6315 16.4467 15.6445 16.3685L15.6641 16.3295L16.1328 14.9037L10.293 12.5599V15.724C10.293 15.8151 10.2637 15.89 10.2051 15.9486C10.1465 16.0072 10.0781 16.0365 10 16.0365C9.92188 16.0365 9.85677 16.0105 9.80469 15.9584C9.7526 15.9063 9.72005 15.8412 9.70703 15.7631V15.724V12.5599L3.86719 14.9037L4.27734 16.1537C4.30339 16.2318 4.29688 16.3067 4.25781 16.3783C4.21875 16.4499 4.16016 16.4987 4.08203 16.5248C4.01693 16.5508 3.94857 16.5476 3.87695 16.515C3.80534 16.4825 3.75651 16.4336 3.73047 16.3685L3.71094 16.3295L2.46094 12.5795L1.17188 13.1068C1.09375 13.1329 1.01562 13.1296 0.9375 13.097C0.859375 13.0645 0.807292 13.0092 0.78125 12.931C0.755208 12.8659 0.755208 12.7976 0.78125 12.726C0.807292 12.6543 0.846354 12.599 0.898438 12.5599L0.9375 12.5404L4.58984 11.0756V6.77869C4.58984 6.01046 4.85677 5.34965 5.39062 4.79626C5.92448 4.24288 6.57552 3.95317 7.34375 3.92712H7.44141H8.41797V1.0365C8.41797 0.789103 8.5026 0.574259 8.67188 0.391968C8.84115 0.209676 9.04948 0.11202 9.29688 0.098999H9.35547H10.6445ZM9.70703 9.68884L9.62891 9.70837L5 11.5638L3.00781 12.3646L3.67188 14.3373L9.70703 11.9154V9.68884ZM10.293 9.68884V11.9154L16.3281 14.3373L16.9922 12.3646L15 11.5638H14.9805L10.293 9.68884ZM12.5586 4.53259H7.44141C6.82943 4.53259 6.30859 4.74418 5.87891 5.16736C5.44922 5.59054 5.22135 6.1016 5.19531 6.70056V6.77869V10.8412L9.41406 9.1615C9.50521 9.12244 9.59961 9.09314 9.69727 9.07361C9.79492 9.05408 9.89583 9.04431 10 9.04431C10.0781 9.04431 10.1595 9.05082 10.2441 9.06384C10.3288 9.07686 10.4102 9.09639 10.4883 9.12244L10.5859 9.1615L14.8047 10.8412V6.77869C14.8047 6.17973 14.5964 5.66215 14.1797 5.22595C13.763 4.78975 13.2487 4.55863 12.6367 4.53259H12.5586ZM12.5586 6.48572C12.6367 6.48572 12.7051 6.51501 12.7637 6.57361C12.8223 6.6322 12.8516 6.70056 12.8516 6.77869C12.8516 6.85681 12.8288 6.92517 12.7832 6.98376C12.7376 7.04236 12.6758 7.07817 12.5977 7.09119H12.5586H7.44141C7.36328 7.09119 7.29492 7.06189 7.23633 7.0033C7.17773 6.9447 7.14844 6.86983 7.14844 6.77869C7.14844 6.71358 7.17122 6.65173 7.2168 6.59314C7.26237 6.53455 7.32422 6.49874 7.40234 6.48572H7.44141H12.5586ZM10.6445 0.704468H9.35547C9.26432 0.704468 9.1862 0.73702 9.12109 0.802124C9.05599 0.867228 9.02344 0.945353 9.02344 1.0365V3.92712H10.9766V1.0365C10.9766 0.945353 10.9473 0.870483 10.8887 0.81189C10.8301 0.753296 10.7617 0.717489 10.6836 0.704468H10.6445Z" fill="#0052FF"/>
                              </svg>
                            </span>
                          </div>
                          <div className="min-w-[60px] text-center">
                            <span className="block text-xl font-bold text-neutral-900">{arrTime}</span>
                            <span className="text-xs text-neutral-600">
                              {firstSegment?.arrivalPort?.name ?? "Arrival"}
                            </span>
                          </div>
                        </div>

                        {/* Column 3: Price & Button */}
                        <div className="flex flex-row items-center justify-between gap-2 border-t pt-4 md:flex-col md:items-end md:justify-center md:border-t-0 md:pt-0">
                          <div className="flex items-baseline gap-2 text-right md:block">
                            <span className="text-2xl font-black text-neutral-900">
                              {formatMoneyFromCents(minPrice)}
                            </span>
                          </div>
                          {typeof deepLink === "string" && searchParams?.isRoundTrip ? (
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedForwardTrip(trip);
                                setShowAllOutboundTrips(false);
                                setTimeout(() => {
                                  returnTripsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                }, 100);
                              }}
                              className={`flex w-full items-center justify-center rounded-lg px-6 py-2.5 text-sm font-semibold shadow-sm transition-colors md:w-auto ${
                                isSelected
                                  ? "border border-[#0052FF] bg-[#F2F7FC80] text-[#0052FF]"
                                  : "bg-blue-600 text-white hover:bg-blue-700"
                              }`}
                            >
                              {isSelected ? "Selected" : "Select"}
                            </button>
                          ) : typeof deepLink === "string" ? (
                            <a
                              href={addAffiliateCode(updateDeepLinkDate(deepLink, searchParams?.date || '')) ?? updateDeepLinkDate(deepLink, searchParams?.date || '')}
                              target="_blank"
                              rel="noreferrer"
                              className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-[12px] bg-blue-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 md:w-auto"
                            >
                              Continue to Ferryhopper
                              <img src="/OuterLink.svg" alt="" className="h-4 w-4" />
                            </a>
                          ) : (
                            <button
                              disabled
                              className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-[12px] bg-blue-600 px-6 py-2.5 text-sm font-semibold text-white opacity-50 md:w-auto"
                            >
                              Continue to Ferryhopper
                              <img src="/OuterLink.svg" alt="" className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Vehicle Support Row */}
                      <div className="col-span-full border-t border-gray-100 px-6 py-3">
                        <div className="flex flex-wrap gap-2">
                          {(() => {
                            const vehicleTypes = getVehicleTypes(trip);
                            if (vehicleTypes.length > 0) {
                              return vehicleTypes.map(type => (
                                <span
                                  key={type}
                                  className="flex items-center gap-1 rounded-full bg-blue-50 px-3 py-1 text-xs text-blue-700"
                                >
                                  {getVehicleEmoji(type)} {type.charAt(0) + type.slice(1).toLowerCase()}s allowed
                                </span>
                              ));
                            }
                            if (hasVehicles) {
                              return (
                                <span className="flex items-center gap-1 rounded-full bg-[#EFF6FF] px-3 py-1 text-xs text-[#1447E6]">
                                  <img src="/CarProfile.svg" alt="" className="h-4 w-4" />
                                  Cars
                                </span>
                              );
                            }
                            return (
                              <span className="flex items-center gap-1 rounded-full  bg-[#EFF6FF] px-3 py-1 text-xs text-[#1447E6]">
                                <img src="/UserCheck.svg" alt="" className="h-4 w-4" />
                                Passengers only
                              </span>
                            );
                          })()}
                          {hasPets && (
                            <span className="flex items-center gap-1 rounded-full bg-[#EFF6FF] px-3 py-1 text-xs text-[#1447E6]">
                              <img src="/PawPrint.svg" alt="" className="h-4 w-4" />
                              Pets
                            </span>
                          )}
                          {hasETicket && (
                            <span className="flex items-center gap-1 rounded-full bg-[#EFF6FF] px-3 py-1 text-xs text-[#1447E6]">
                              <img src="/Ticket.svg" alt="" className="h-4 w-4" />
                              E-Ticket
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Accommodations Section (Expandable) */}
                      {isExpanded && topAccommodations.length > 0 && (
                        <div className="px-6 pb-6 pt-0">
                          <div className="mb-4 h-px w-full bg-gray-100"></div>
                          <div className="mb-3 text-xs font-semibold text-neutral-700">
                            Available accommodation options:
                          </div>
                          <div className="grid grid-cols-1 gap-x-12 gap-y-3 sm:grid-cols-2">
                            {topAccommodations.map((item, idx) => (
                              <div
                                key={`${item.description}-${idx}`}
                                className="flex items-center justify-between text-sm"
                              >
                                <span className="text-neutral-600">
                                  {isCabin(item.description) && '🛏️ '}
                                  {item.description}
                                </span>
                                <span className="font-medium text-neutral-900">
                                  {formatMoneyFromCents(item.price)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Footer */}
                      <div className={`flex flex-wrap items-center justify-between gap-4 border-t px-6 py-3 transition-colors duration-300 group-hover:border-gray-100 group-hover:bg-[#E4F2FF80] ${
                        isSelected
                          ? "border-gray-100 bg-[#E4F2FF80]"
                          : isCheapest
                          ? "border-green-200 bg-green-100/40"
                          : "border-gray-100 bg-[#fafafa]"
                      }`}>
                        <button
                          className="flex items-center gap-1 text-xs font-semibold text-neutral-600 transition-colors hover:text-neutral-900"
                          onClick={() => setExpandedTripIndex(isExpanded ? null : index)}
                        >
                          {isExpanded ? "Hide" : "Show"} accommodations
                          <span className="text-[16px]">{isExpanded ? "▲" : "▼"}</span>
                        </button>
                      </div>
                    </div>
                  );
                });
              })()
            )}
          </div>

          {outboundCollapsed && selectedForwardTrip && (() => {
            const outboundSummary = getTripSummary(selectedForwardTrip);
            if (!outboundSummary) return null;
            return (
              <button
                type="button"
                onClick={() => setShowAllOutboundTrips(true)}
                className="flex items-center gap-1 self-center text-sm font-semibold text-blue-600 hover:text-blue-700 hover:underline"
              >
                View all results for {outboundSummary.depPort} - {outboundSummary.arrPort}
                <span className="text-xs">▾</span>
              </button>
            );
          })()}
          </>
        </div>

        {/* Return Trips Section - Show only for round trips */}
        {isRoundTripSearch && searchParams && (
          <div ref={returnTripsRef} className="flex flex-col gap-5">
            <div className="flex flex-col gap-4 px-2 md:flex-row md:items-center md:justify-between">
              <h3 className="text-xl font-bold text-neutral-900">
                {searchParams.arrivalPort} → {searchParams.departurePort}
              </h3>

              {/* Sort Dropdown */}
              <div className="flex items-center gap-2">
                <span className="hidden text-sm text-neutral-600 lg:block">Sort by:</span>
                <select
                  className="cursor-pointer border-none bg-transparent py-0 pr-8 text-sm font-semibold text-neutral-900 focus:ring-0"
                  value={returnSortBy}
                  onChange={(e) => setReturnSortBy(e.target.value as "recommended" | "price" | "duration")}
                >
                  <option value="recommended">Recommended</option>
                  <option value="price">Price (Low to High)</option>
                  <option value="duration">Duration (Shortest)</option>
                </select>
              </div>
            </div>

            <>
            <div className="space-y-5">
              {isLoadingReturnTrips ? (
                <>
                  {[1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="animate-pulse overflow-hidden rounded-xl border border-gray-100 bg-white p-5 shadow-sm md:p-6"
                    >
                      <div className="grid grid-cols-1 items-center gap-6 md:grid-cols-[1.5fr_2fr_1fr]">
                        <div className="flex items-center gap-4">
                          <div className="h-12 w-12 rounded-lg bg-gray-200"></div>
                          <div className="flex flex-col gap-2 w-32">
                            <div className="h-4 w-full rounded bg-gray-200"></div>
                            <div className="h-3 w-2/3 rounded bg-gray-200"></div>
                          </div>
                        </div>
                        <div className="flex items-center justify-between gap-4">
                          <div className="h-4 w-16 rounded bg-gray-200"></div>
                          <div className="h-4 w-24 rounded bg-gray-200"></div>
                          <div className="h-4 w-16 rounded bg-gray-200"></div>
                        </div>
                        <div className="flex flex-col items-end gap-3">
                          <div className="h-6 w-20 rounded bg-gray-200"></div>
                          <div className="h-10 w-32 rounded-lg bg-gray-200"></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              ) : returnTrips.length === 0 ? (
                <div className="rounded-xl border border-dashed border-green-200 bg-green-50 p-8 text-center">
                  <div className="mx-auto mb-3 text-4xl">🔄</div>
                  <div className="text-sm font-semibold text-green-900">
                    No return trips found for {searchParams.returnDate ? formatDateDisplay(searchParams.returnDate) : 'selected date'}
                  </div>
                  <div className="mt-1 text-xs text-green-700">
                    Try selecting a different return date
                  </div>
                </div>
              ) : (
                (() => {
                  const allReturnPrices = sortedReturnTrips
                    .map((t) => minAccommodationPriceCents(t))
                    .filter((p): p is number => p !== null);
                  const cheapestReturnPrice = allReturnPrices.length > 0 ? Math.min(...allReturnPrices) : null;
                  const visibleReturnTrips = returnCollapsed
                    ? (selectedReturnTrip ? [selectedReturnTrip] : [])
                    : sortedReturnTrips;

                  return visibleReturnTrips.map((trip, index) => {
                    const segments = (trip as { segments?: Array<Record<string, unknown>> }).segments ?? [];
                    const firstSegment = segments[0] as
                      | {
                          departurePort?: { name?: string };
                          arrivalPort?: { name?: string };
                          departureDateTime?: string;
                          arrivalDateTime?: string;
                          ownerCompany?: { name?: string; iconURL?: string };
                          marketingCompany?: { name?: string; iconURL?: string };
                          vessel?: { name?: string };
                          accommodations?: Array<Record<string, unknown>>;
                        }
                      | undefined;

                    const deepLink = (trip as { deepLink?: string }).deepLink;
                    const minPrice = minAccommodationPriceCents(trip);
                    const isCheapest = minPrice !== null && minPrice === cheapestReturnPrice && returnTrips.length > 1;
                    const hasVehicles = (trip as { hasVehicles?: boolean }).hasVehicles === true;
                    const hasPets = (trip as { hasPets?: boolean }).hasPets === true;
                    const hasETicket = (trip as { hasETicket?: boolean }).hasETicket === true;
                    const accommodations = firstSegment?.accommodations ?? [];
                    const topAccommodations = accommodations.slice(0, 4).map((item) => {
                      const type = (item as { type?: string }).type;
                      const description = type ? formatAccommodationType(type) : "Accommodation";
                      const price =
                        (item as { expectedPrice?: { totalPriceInCents?: number } })
                          .expectedPrice?.totalPriceInCents ?? null;
                      return { description, price };
                    });
                    const isExpanded = expandedReturnTripIndex === index;
                    const duration = calculateDuration(firstSegment?.departureDateTime, firstSegment?.arrivalDateTime);
                    const depTime = formatTime(firstSegment?.departureDateTime);
                    const arrTime = formatTime(firstSegment?.arrivalDateTime);
                    const isSelected = selectedReturnTrip === trip;
                    const canSelectReturn = selectedForwardTrip !== null;

                  return (
                    <div
                      key={`return-${index}-${firstSegment?.departureDateTime ?? "trip"}`}
                      className={`group relative overflow-hidden rounded-xl border shadow-sm transition-colors duration-300 hover:shadow-md hover:border-[#0052FF] hover:bg-[#F2F7FC80] ${
                        isSelected
                          ? "border-[#0052FF] bg-[#F2F7FC80]"
                          : isCheapest
                          ? "border-green-500 bg-green-50"
                          : "border-green-200 bg-white"
                      }`}
                    >
                      {/* Best Price Badge */}
                      {isCheapest && (
                        <div className="absolute left-0 top-0 z-10 rounded-br-lg border-b border-r border-green-200 bg-white/95 px-3 py-1.5 shadow-sm">
                          <div className="text-[11px] font-bold uppercase tracking-wider text-green-700">
                            💰 Best Price
                          </div>
                        </div>
                      )}

                      {/* Main Card Content */}
                      <div className={`grid grid-cols-1 items-center gap-6 p-5 md:grid-cols-[1.5fr_2fr_1fr] md:p-6 ${isCheapest ? 'pt-8 md:pt-6' : ''}`}>
                        {/* Column 1: Company */}
                        <div className="flex items-center gap-4">
                          <div className="flex h-12 w-12 items-center justify-center rounded-lg border border-gray-100 bg-white">
                            {firstSegment?.marketingCompany?.iconURL ? (
                              <img
                                src={firstSegment.marketingCompany.iconURL}
                                alt={firstSegment.marketingCompany?.name ?? firstSegment?.ownerCompany?.name ?? "Company"}
                                className="h-full w-full bg-contain bg-center bg-no-repeat object-contain"
                              />
                            ) : (
                              <div className="text-2xl">
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M15.957 18.0677C16.4388 18.5235 16.9922 18.8588 17.6172 19.0736C18.2422 19.2885 18.8867 19.3503 19.5508 19.2592C19.6289 19.2592 19.7005 19.2819 19.7656 19.3275C19.8307 19.3731 19.8698 19.4414 19.8828 19.5326C19.8958 19.6107 19.8763 19.6823 19.8242 19.7474C19.7721 19.8125 19.7005 19.8516 19.6094 19.8646C18.9453 19.9427 18.2878 19.8874 17.6367 19.6986C16.9857 19.5098 16.3932 19.2071 15.8594 18.7904L15.7617 18.6927L15.6641 18.7904C15.2604 19.155 14.8177 19.4284 14.3359 19.6107C13.8411 19.78 13.3398 19.8646 12.832 19.8646C12.3242 19.8646 11.8294 19.767 11.3477 19.5717C10.8659 19.3894 10.4297 19.1159 10.0391 18.7513L10 18.7123L9.92188 18.7904C9.51823 19.155 9.06901 19.4284 8.57422 19.6107C8.07943 19.78 7.58138 19.8646 7.08008 19.8646C6.57878 19.8646 6.08724 19.767 5.60547 19.5717C5.11068 19.3894 4.66797 19.1159 4.27734 18.7513L4.23828 18.6927L4.14062 18.7904C3.63281 19.1941 3.06641 19.4903 2.44141 19.6791C1.81641 19.8679 1.17839 19.9362 0.527344 19.8842L0.371094 19.8646C0.292969 19.8516 0.227865 19.8125 0.175781 19.7474C0.123698 19.6823 0.104167 19.6107 0.117188 19.5326C0.130208 19.4414 0.169271 19.3731 0.234375 19.3275C0.299479 19.2819 0.371094 19.2592 0.449219 19.2592C1.11328 19.3503 1.75781 19.2885 2.38281 19.0736C3.00781 18.8588 3.5612 18.5235 4.04297 18.0677C4.10807 18.0157 4.18294 17.9896 4.26758 17.9896C4.35221 17.9896 4.42057 18.0222 4.47266 18.0873C5.13672 18.8164 5.96029 19.2071 6.94336 19.2592C7.92643 19.3112 8.78255 19.0052 9.51172 18.3412C9.5638 18.3021 9.60938 18.2598 9.64844 18.2142C9.6875 18.1687 9.73307 18.1263 9.78516 18.0873C9.83724 18.0222 9.90885 17.9896 10 17.9896C10.0911 17.9896 10.1628 18.0222 10.2148 18.0873C10.8789 18.8164 11.7025 19.2071 12.6855 19.2592C13.6686 19.3112 14.5312 19.0052 15.2734 18.3412L15.4004 18.2142L15.5273 18.0873C15.5794 18.0222 15.6478 17.9896 15.7324 17.9896C15.8171 17.9896 15.8919 18.0157 15.957 18.0677ZM10.6445 0.098999C10.9049 0.098999 11.1263 0.190145 11.3086 0.372437C11.4909 0.554728 11.582 0.776082 11.582 1.0365V3.92712H12.5586C13.3268 3.92712 13.9876 4.19731 14.541 4.73767C15.0944 5.27804 15.3841 5.93233 15.4102 6.70056V6.77869V11.0756L19.0625 12.5404C19.1276 12.5795 19.1797 12.6348 19.2188 12.7064C19.2578 12.778 19.2578 12.8529 19.2188 12.931C19.1927 13.0092 19.1471 13.0645 19.082 13.097C19.0169 13.1296 18.9453 13.1329 18.8672 13.1068H18.8281L17.5391 12.5795L16.2305 16.5248C16.2044 16.6029 16.1523 16.6615 16.0742 16.7006C15.9961 16.7396 15.918 16.7461 15.8398 16.7201C15.7747 16.6941 15.7227 16.6485 15.6836 16.5834C15.6445 16.5183 15.6315 16.4467 15.6445 16.3685L15.6641 16.3295L16.1328 14.9037L10.293 12.5599V15.724C10.293 15.8151 10.2637 15.89 10.2051 15.9486C10.1465 16.0072 10.0781 16.0365 10 16.0365C9.92188 16.0365 9.85677 16.0105 9.80469 15.9584C9.7526 15.9063 9.72005 15.8412 9.70703 15.7631V15.724V12.5599L3.86719 14.9037L4.27734 16.1537C4.30339 16.2318 4.29688 16.3067 4.25781 16.3783C4.21875 16.4499 4.16016 16.4987 4.08203 16.5248C4.01693 16.5508 3.94857 16.5476 3.87695 16.515C3.80534 16.4825 3.75651 16.4336 3.73047 16.3685L3.71094 16.3295L2.46094 12.5795L1.17188 13.1068C1.09375 13.1329 1.01562 13.1296 0.9375 13.097C0.859375 13.0645 0.807292 13.0092 0.78125 12.931C0.755208 12.8659 0.755208 12.7976 0.78125 12.726C0.807292 12.6543 0.846354 12.599 0.898438 12.5599L0.9375 12.5404L4.58984 11.0756V6.77869C4.58984 6.01046 4.85677 5.34965 5.39062 4.79626C5.92448 4.24288 6.57552 3.95317 7.34375 3.92712H7.44141H8.41797V1.0365C8.41797 0.789103 8.5026 0.574259 8.67188 0.391968C8.84115 0.209676 9.04948 0.11202 9.29688 0.098999H9.35547H10.6445ZM9.70703 9.68884L9.62891 9.70837L5 11.5638L3.00781 12.3646L3.67188 14.3373L9.70703 11.9154V9.68884ZM10.293 9.68884V11.9154L16.3281 14.3373L16.9922 12.3646L15 11.5638H14.9805L10.293 9.68884ZM12.5586 4.53259H7.44141C6.82943 4.53259 6.30859 4.74418 5.87891 5.16736C5.44922 5.59054 5.22135 6.1016 5.19531 6.70056V6.77869V10.8412L9.41406 9.1615C9.50521 9.12244 9.59961 9.09314 9.69727 9.07361C9.79492 9.05408 9.89583 9.04431 10 9.04431C10.0781 9.04431 10.1595 9.05082 10.2441 9.06384C10.3288 9.07686 10.4102 9.09639 10.4883 9.12244L10.5859 9.1615L14.8047 10.8412V6.77869C14.8047 6.17973 14.5964 5.66215 14.1797 5.22595C13.763 4.78975 13.2487 4.55863 12.6367 4.53259H12.5586ZM12.5586 6.48572C12.6367 6.48572 12.7051 6.51501 12.7637 6.57361C12.8223 6.6322 12.8516 6.70056 12.8516 6.77869C12.8516 6.85681 12.8288 6.92517 12.7832 6.98376C12.7376 7.04236 12.6758 7.07817 12.5977 7.09119H12.5586H7.44141C7.36328 7.09119 7.29492 7.06189 7.23633 7.0033C7.17773 6.9447 7.14844 6.86983 7.14844 6.77869C7.14844 6.71358 7.17122 6.65173 7.2168 6.59314C7.26237 6.53455 7.32422 6.49874 7.40234 6.48572H7.44141H12.5586ZM10.6445 0.704468H9.35547C9.26432 0.704468 9.1862 0.73702 9.12109 0.802124C9.05599 0.867228 9.02344 0.945353 9.02344 1.0365V3.92712H10.9766V1.0365C10.9766 0.945353 10.9473 0.870483 10.8887 0.81189C10.8301 0.753296 10.7617 0.717489 10.6836 0.704468H10.6445Z" fill="#0052FF"/>
                                </svg>
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col">
                            <span className="text-base font-bold text-neutral-900">
                              {firstSegment?.ownerCompany?.name ?? "Ferry Company"}
                            </span>
                            <span className="text-xs text-neutral-600">
                              {firstSegment?.vessel?.name ?? ""}
                            </span>
                          </div>
                        </div>

                        {/* Column 2: Timeline */}
                        <div className="flex items-center justify-between gap-4 px-2">
                          <div className="min-w-[60px] text-center">
                            <span className="block text-xl font-bold text-neutral-900">{depTime}</span>
                            <span className="text-xs text-neutral-600">
                              {firstSegment?.departurePort?.name ?? "Departure"}
                            </span>
                            {firstSegment?.departureDateTime && (
                              <span className="block text-[10px] text-neutral-400 mt-0.5">
                                {new Date(firstSegment.departureDateTime).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </span>
                            )}
                          </div>
                          <div className="flex flex-1 flex-col items-center gap-1">
                            <span className="text-xs text-neutral-600">{duration}</span>
                            <div className="relative h-[2px] w-full bg-gray-200">
                              <div className="absolute -top-[3px] left-0 h-2 w-2 rounded-full bg-gray-300"></div>
                              <div className="absolute -top-[3px] right-0 h-2 w-2 rounded-full bg-gray-300"></div>
                            </div>
                            <span className="text-base text-gray-300">
                              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M15.957 18.0677C16.4388 18.5235 16.9922 18.8588 17.6172 19.0736C18.2422 19.2885 18.8867 19.3503 19.5508 19.2592C19.6289 19.2592 19.7005 19.2819 19.7656 19.3275C19.8307 19.3731 19.8698 19.4414 19.8828 19.5326C19.8958 19.6107 19.8763 19.6823 19.8242 19.7474C19.7721 19.8125 19.7005 19.8516 19.6094 19.8646C18.9453 19.9427 18.2878 19.8874 17.6367 19.6986C16.9857 19.5098 16.3932 19.2071 15.8594 18.7904L15.7617 18.6927L15.6641 18.7904C15.2604 19.155 14.8177 19.4284 14.3359 19.6107C13.8411 19.78 13.3398 19.8646 12.832 19.8646C12.3242 19.8646 11.8294 19.767 11.3477 19.5717C10.8659 19.3894 10.4297 19.1159 10.0391 18.7513L10 18.7123L9.92188 18.7904C9.51823 19.155 9.06901 19.4284 8.57422 19.6107C8.07943 19.78 7.58138 19.8646 7.08008 19.8646C6.57878 19.8646 6.08724 19.767 5.60547 19.5717C5.11068 19.3894 4.66797 19.1159 4.27734 18.7513L4.23828 18.6927L4.14062 18.7904C3.63281 19.1941 3.06641 19.4903 2.44141 19.6791C1.81641 19.8679 1.17839 19.9362 0.527344 19.8842L0.371094 19.8646C0.292969 19.8516 0.227865 19.8125 0.175781 19.7474C0.123698 19.6823 0.104167 19.6107 0.117188 19.5326C0.130208 19.4414 0.169271 19.3731 0.234375 19.3275C0.299479 19.2819 0.371094 19.2592 0.449219 19.2592C1.11328 19.3503 1.75781 19.2885 2.38281 19.0736C3.00781 18.8588 3.5612 18.5235 4.04297 18.0677C4.10807 18.0157 4.18294 17.9896 4.26758 17.9896C4.35221 17.9896 4.42057 18.0222 4.47266 18.0873C5.13672 18.8164 5.96029 19.2071 6.94336 19.2592C7.92643 19.3112 8.78255 19.0052 9.51172 18.3412C9.5638 18.3021 9.60938 18.2598 9.64844 18.2142C9.6875 18.1687 9.73307 18.1263 9.78516 18.0873C9.83724 18.0222 9.90885 17.9896 10 17.9896C10.0911 17.9896 10.1628 18.0222 10.2148 18.0873C10.8789 18.8164 11.7025 19.2071 12.6855 19.2592C13.6686 19.3112 14.5312 19.0052 15.2734 18.3412L15.4004 18.2142L15.5273 18.0873C15.5794 18.0222 15.6478 17.9896 15.7324 17.9896C15.8171 17.9896 15.8919 18.0157 15.957 18.0677ZM10.6445 0.098999C10.9049 0.098999 11.1263 0.190145 11.3086 0.372437C11.4909 0.554728 11.582 0.776082 11.582 1.0365V3.92712H12.5586C13.3268 3.92712 13.9876 4.19731 14.541 4.73767C15.0944 5.27804 15.3841 5.93233 15.4102 6.70056V6.77869V11.0756L19.0625 12.5404C19.1276 12.5795 19.1797 12.6348 19.2188 12.7064C19.2578 12.778 19.2578 12.8529 19.2188 12.931C19.1927 13.0092 19.1471 13.0645 19.082 13.097C19.0169 13.1296 18.9453 13.1329 18.8672 13.1068H18.8281L17.5391 12.5795L16.2305 16.5248C16.2044 16.6029 16.1523 16.6615 16.0742 16.7006C15.9961 16.7396 15.918 16.7461 15.8398 16.7201C15.7747 16.6941 15.7227 16.6485 15.6836 16.5834C15.6445 16.5183 15.6315 16.4467 15.6445 16.3685L15.6641 16.3295L16.1328 14.9037L10.293 12.5599V15.724C10.293 15.8151 10.2637 15.89 10.2051 15.9486C10.1465 16.0072 10.0781 16.0365 10 16.0365C9.92188 16.0365 9.85677 16.0105 9.80469 15.9584C9.7526 15.9063 9.72005 15.8412 9.70703 15.7631V15.724V12.5599L3.86719 14.9037L4.27734 16.1537C4.30339 16.2318 4.29688 16.3067 4.25781 16.3783C4.21875 16.4499 4.16016 16.4987 4.08203 16.5248C4.01693 16.5508 3.94857 16.5476 3.87695 16.515C3.80534 16.4825 3.75651 16.4336 3.73047 16.3685L3.71094 16.3295L2.46094 12.5795L1.17188 13.1068C1.09375 13.1329 1.01562 13.1296 0.9375 13.097C0.859375 13.0645 0.807292 13.0092 0.78125 12.931C0.755208 12.8659 0.755208 12.7976 0.78125 12.726C0.807292 12.6543 0.846354 12.599 0.898438 12.5599L0.9375 12.5404L4.58984 11.0756V6.77869C4.58984 6.01046 4.85677 5.34965 5.39062 4.79626C5.92448 4.24288 6.57552 3.95317 7.34375 3.92712H7.44141H8.41797V1.0365C8.41797 0.789103 8.5026 0.574259 8.67188 0.391968C8.84115 0.209676 9.04948 0.11202 9.29688 0.098999H9.35547H10.6445ZM9.70703 9.68884L9.62891 9.70837L5 11.5638L3.00781 12.3646L3.67188 14.3373L9.70703 11.9154V9.68884ZM10.293 9.68884V11.9154L16.3281 14.3373L16.9922 12.3646L15 11.5638H14.9805L10.293 9.68884ZM12.5586 4.53259H7.44141C6.82943 4.53259 6.30859 4.74418 5.87891 5.16736C5.44922 5.59054 5.22135 6.1016 5.19531 6.70056V6.77869V10.8412L9.41406 9.1615C9.50521 9.12244 9.59961 9.09314 9.69727 9.07361C9.79492 9.05408 9.89583 9.04431 10 9.04431C10.0781 9.04431 10.1595 9.05082 10.2441 9.06384C10.3288 9.07686 10.4102 9.09639 10.4883 9.12244L10.5859 9.1615L14.8047 10.8412V6.77869C14.8047 6.17973 14.5964 5.66215 14.1797 5.22595C13.763 4.78975 13.2487 4.55863 12.6367 4.53259H12.5586ZM12.5586 6.48572C12.6367 6.48572 12.7051 6.51501 12.7637 6.57361C12.8223 6.6322 12.8516 6.70056 12.8516 6.77869C12.8516 6.85681 12.8288 6.92517 12.7832 6.98376C12.7376 7.04236 12.6758 7.07817 12.5977 7.09119H12.5586H7.44141C7.36328 7.09119 7.29492 7.06189 7.23633 7.0033C7.17773 6.9447 7.14844 6.86983 7.14844 6.77869C7.14844 6.71358 7.17122 6.65173 7.2168 6.59314C7.26237 6.53455 7.32422 6.49874 7.40234 6.48572H7.44141H12.5586ZM10.6445 0.704468H9.35547C9.26432 0.704468 9.1862 0.73702 9.12109 0.802124C9.05599 0.867228 9.02344 0.945353 9.02344 1.0365V3.92712H10.9766V1.0365C10.9766 0.945353 10.9473 0.870483 10.8887 0.81189C10.8301 0.753296 10.7617 0.717489 10.6836 0.704468H10.6445Z" fill="#0052FF"/>
                              </svg>
                            </span>
                          </div>
                          <div className="min-w-[60px] text-center">
                            <span className="block text-xl font-bold text-neutral-900">{arrTime}</span>
                            <span className="text-xs text-neutral-600">
                              {firstSegment?.arrivalPort?.name ?? "Arrival"}
                            </span>
                          </div>
                        </div>

                        {/* Column 3: Price & Button */}
                        <div className="flex flex-row items-center justify-between gap-2 border-t pt-4 md:flex-col md:items-end md:justify-center md:border-t-0 md:pt-0">
                          <div className="flex items-baseline gap-2 text-right md:block">
                            <span className="text-2xl font-black text-neutral-900">
                              {formatMoneyFromCents(minPrice)}
                            </span>
                          </div>
                          {typeof deepLink === "string" && canSelectReturn ? (
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedReturnTrip(trip);
                                setShowAllReturnTrips(false);
                              }}
                              className={`flex w-full items-center justify-center rounded-lg px-6 py-2.5 text-sm font-semibold shadow-sm transition-colors md:w-auto ${
                                isSelected
                                  ? "border border-[#0052FF] bg-[#F2F7FC80] text-[#0052FF]"
                                  : "bg-blue-600 text-white hover:bg-blue-700"
                              }`}
                            >
                              {isSelected ? "Selected" : "Select"}
                            </button>
                          ) : (
                            <button
                              disabled
                              title={!canSelectReturn ? "Select your outbound trip first" : undefined}
                              className="w-full rounded-lg bg-blue-600 px-6 py-2.5 text-sm font-semibold text-white opacity-50 md:w-auto"
                            >
                              Select
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Vehicle Support Row */}
                      <div className="col-span-full border-t border-gray-100 px-6 py-3">
                        <div className="flex flex-wrap gap-2">
                          {(() => {
                            const vehicleTypes = getVehicleTypes(trip);
                            if (vehicleTypes.length > 0) {
                              return vehicleTypes.map(type => (
                                <span
                                  key={type}
                                  className="flex items-center gap-1 rounded-full bg-blue-50 px-3 py-1 text-xs text-blue-700"
                                >
                                  {getVehicleEmoji(type)} {type.charAt(0) + type.slice(1).toLowerCase()}s allowed
                                </span>
                              ));
                            }
                            if (hasVehicles) {
                              return (
                                <span className="flex items-center gap-1 rounded-full bg-[#EFF6FF]  px-3 py-1 text-xs text-[#1447E6]">
                                  <img src="/CarProfile.svg" alt="" className="h-4 w-4" />
                                  Cars
                                </span>
                              );
                            }
                            return (
                              <span className="flex items-center gap-1 rounded-full bg-[#EFF6FF]  px-3 py-1 text-xs text-[#1447E6]">
                                <img src="/UserCheck.svg" alt="" className="h-4 w-4" />
                                Passengers only
                              </span>
                            );
                          })()}
                          {hasPets && (
                            <span className="flex items-center gap-1 rounded-full bg-[#EFF6FF] px-3 py-1 text-xs text-[#1447E6]">
                              <img src="/PawPrint.svg" alt="" className="h-4 w-4" />
                              Pets
                            </span>
                          )}
                          {hasETicket && (
                            <span className="flex items-center gap-1 rounded-full bg-[#EFF6FF] px-3 py-1 text-xs text-[#1447E6]">
                              <img src="/Ticket.svg" alt="" className="h-4 w-4" />
                              E-Ticket
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Accommodations Section (Expandable) */}
                      {isExpanded && topAccommodations.length > 0 && (
                        <div className="px-6 pb-6 pt-0">
                          <div className="mb-4 h-px w-full bg-gray-100"></div>
                          <div className="mb-3 text-xs font-semibold text-neutral-700">
                            Available accommodation options:
                          </div>
                          <div className="grid grid-cols-1 gap-x-12 gap-y-3 sm:grid-cols-2">
                            {topAccommodations.map((item, idx) => (
                              <div
                                key={`${item.description}-${idx}`}
                                className="flex items-center justify-between text-sm"
                              >
                                <span className="text-neutral-600">
                                  {isCabin(item.description) && '🛏️ '}
                                  {item.description}
                                </span>
                                <span className="font-medium text-neutral-900">
                                  {formatMoneyFromCents(item.price)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Footer */}
                      <div className={`flex flex-wrap items-center justify-between gap-4 border-t px-6 py-3 transition-colors duration-300 group-hover:border-gray-100 group-hover:bg-[#E4F2FF80] ${
                        isSelected
                          ? "border-gray-100 bg-[#E4F2FF80]"
                          : isCheapest
                          ? "border-green-200 bg-green-100/40"
                          : "border-gray-100 bg-[#fafafa]"
                      }`}>
                        <button
                          className="flex items-center gap-1 text-xs font-semibold text-neutral-600 transition-colors hover:text-neutral-900"
                          onClick={() => setExpandedReturnTripIndex(isExpanded ? null : index)}
                        >
                          {isExpanded ? "Hide" : "Show"} accommodations
                          <span className="text-[16px]">{isExpanded ? "▲" : "▼"}</span>
                        </button>
                      </div>
                    </div>
                  );
                });
              })()
            )}
            </div>

            {returnCollapsed && selectedReturnTrip && (() => {
              const returnSummary = getTripSummary(selectedReturnTrip);
              if (!returnSummary) return null;
              return (
                <button
                  type="button"
                  onClick={() => setShowAllReturnTrips(true)}
                  className="flex items-center gap-1 self-center text-sm font-semibold text-blue-600 hover:text-blue-700 hover:underline"
                >
                  View all results for {returnSummary.depPort} - {returnSummary.arrPort}
                  <span className="text-xs">▾</span>
                </button>
              );
            })()}
            </>

          </div>
        )}
            </>
          );

          if (!isRoundTripSearch) {
            return <div className="mt-8">{tripsAndReturnContent}</div>;
          }

          return (
            <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start">
              <div className="flex flex-col gap-8">{tripsAndReturnContent}</div>
              <aside className="lg:sticky lg:top-6 lg:self-start">
                <TripSummarySidebar
                  outboundSummary={getTripSummary(selectedForwardTrip)}
                  returnSummary={getTripSummary(selectedReturnTrip)}
                  outboundDateLabel={searchParams?.date ? formatDateDisplay(searchParams.date) : ""}
                  returnDateLabel={searchParams?.returnDate ? formatDateDisplay(searchParams.returnDate) : ""}
                  departurePort={searchParams?.departurePort ?? ""}
                  arrivalPort={searchParams?.arrivalPort ?? ""}
                  combinedRoundTripUrl={combinedRoundTripUrl}
                />
              </aside>
            </div>
          );
        })()}

        {/* Recording Modal */}
        {showRecordingModal && (
          <div className="fixed inset-0 z-50 flex items-start lg:items-center justify-center bg-black bg-opacity-50">
            <div className="relative w-full max-w-md rounded-2xl bg-white p-8 shadow-2xl">
              {/* Close button */}
              <button
                onClick={stopRecording}
                className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {/* Recording Animation */}
              <div className="flex flex-col items-center justify-center space-y-6">
                <div className="relative">
                  {/* Pulsing circles */}
                  <div className="absolute inset-0 animate-ping rounded-full bg-red-400 opacity-75"></div>
                  <div className="absolute inset-0 animate-pulse rounded-full bg-red-500 opacity-50"></div>

                  {/* Microphone icon */}
                  <div className="relative flex h-24 w-24 items-center justify-center rounded-full bg-red-500 text-white shadow-lg">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      className="h-12 w-12"
                    >
                      <path d="M8.25 4.5a3.75 3.75 0 1 1 7.5 0v8.25a3.75 3.75 0 1 1-7.5 0V4.5Z" />
                      <path d="M6 10.5a.75.75 0 0 1 .75.75v1.5a5.25 5.25 0 1 0 10.5 0v-1.5a.75.75 0 0 1 1.5 0v1.5a6.751 6.751 0 0 1-6 6.709v2.291h3a.75.75 0 0 1 0 1.5h-7.5a.75.75 0 0 1 0-1.5h3v-2.291a6.751 6.751 0 0 1-6-6.709v-1.5A.75.75 0 0 1 6 10.5Z" />
                    </svg>
                  </div>
                </div>

                {/* Recording status */}
                <div className="text-center">
                  <h3 className="text-xl font-bold text-neutral-900">Listening...</h3>
                  <p className="mt-2 text-sm text-neutral-600">Speak your message clearly</p>
                </div>

                {/* Duration */}
                <div className="flex items-center space-x-2 text-lg font-mono text-neutral-700">
                  <span className="h-3 w-3 animate-pulse rounded-full bg-red-500"></span>
                  <span>{Math.floor(recordingDuration / 60)}:{String(recordingDuration % 60).padStart(2, '0')}</span>
                </div>

                {/* Audio Level Visualizer */}
                <div className="w-full rounded-lg bg-gray-50 p-4 border border-gray-200">
                  <div className="flex items-end justify-center space-x-1 h-12 mb-3">
                    {[...Array(12)].map((_, i) => {
                      const barHeight = Math.max(20, audioLevel * (0.5 + Math.random() * 0.5));
                      return (
                        <div
                          key={i}
                          className="w-2 bg-gradient-to-t from-red-500 to-red-300 rounded-t transition-all duration-100"
                          style={{
                            height: `${barHeight}%`,
                          }}
                        ></div>
                      );
                    })}
                  </div>

                  {/* Live Transcript */}
                  {liveTranscript ? (
                    <div className="min-h-[60px] max-h-32 overflow-y-auto bg-white rounded p-3 border border-gray-200">
                      <p className="text-sm text-neutral-700 leading-relaxed">
                        {liveTranscript}
                      </p>
                    </div>
                  ) : (
                    <div className="min-h-[60px] bg-white rounded p-3 border border-gray-200 flex items-center justify-center">
                      <p className="text-sm text-neutral-400 italic">
                        {audioLevel > 5 ? 'Listening... I can hear you!' : 'Start speaking...'}
                      </p>
                    </div>
                  )}
                </div>

                {/* Stop button */}
                <button
                  onClick={stopRecording}
                  className="mt-4 rounded-full bg-red-500 px-8 py-3 font-semibold text-white shadow-lg transition-all hover:bg-red-600 hover:shadow-xl"
                >
                  Stop
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
